import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, Package, Users, MapPin, BarChart3, Plus, Pencil, Trash2, Check, X, ShoppingBag, Crown, TrendingUp, Upload, Search, Download, Eye, Activity, GraduationCap, Sparkles, Settings, Save, Database, ExternalLink, RefreshCw, ArrowRight, Star, Receipt, Mail, ClipboardList, Gift, Anchor, GripVertical, ChevronUp, ChevronDown, Megaphone, Truck, Copy, Ban, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ColorPicker, SizePicker } from '../components/ColorSizePicker';
import { ImageUpload, StatCard, API_URL, SupplierPicker } from './admin/shared';
import CatalogSyncTab from './admin/CatalogSyncTab';
import BrowseAutomationTab from './admin/BrowseAutomationTab';
import BrandsTab from './admin/BrandsTab';
import InvoicesTab from './admin/InvoicesTab';
import SupplierInvoicesTab from './admin/SupplierInvoicesTab';
import CommunicationsTab from './admin/CommunicationsTab';
import TripInquiriesTab from './admin/TripInquiriesTab';
import { useReorderable, ReorderHandle, ReorderSaveBar } from '../components/ReorderControls';
import FormsTab from './admin/FormsTab';
import HomepageTab from './admin/HomepageTab';
import PopupsTab from './admin/PopupsTab';
import SuppliersTab from './admin/SuppliersTab';
import NotificationRecipientsCard from './admin/NotificationRecipientsCard';
import PromosTab from './admin/PromosTab';
import ReferralsTab from './admin/ReferralsTab';
import MembershipsTab from './admin/MembershipsTab';
import PageHeadersTab from './admin/PageHeadersTab';
import PlannerOperatorsTab from './admin/PlannerOperatorsTab';
import NewslettersTab from './admin/NewslettersTab';
import ManualOrderModal from './admin/ManualOrderModal';
import VisitUsLocationsCard from './admin/VisitUsLocationsCard';
import ContactUsItemsCard from './admin/ContactUsItemsCard';
import TripBookingsModal from './admin/TripBookingsModal';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const AdminPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  // Reset the multi-select whenever the tab changes — selections in New Gear
  // shouldn't leak into Pre-Dived and vice-versa.
  useEffect(() => {
    setSelectedProductIds(new Set());
  }, [activeTab]);
  const [loading, setLoading] = useState(true);
  const [automationStats, setAutomationStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberDateFrom, setMemberDateFrom] = useState('');
  const [memberDateTo, setMemberDateTo] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [applyPromoOrder, setApplyPromoOrder] = useState(null);
  const [applyPromoCredits, setApplyPromoCredits] = useState([]);
  const [selectedCreditId, setSelectedCreditId] = useState('');
  const [forceApplyEnabled, setForceApplyEnabled] = useState(false);
  const [forceItemIndex, setForceItemIndex] = useState(null);
  const [promoForm, setPromoForm] = useState({
    type: 'free_item', // 'free_item' | 'fixed_amount'
    free_item_id: '',
    free_item_label: '',
    amount: '',
    applies_to_category: '',
    expires_at: '',
    reason: '',
  });
  const [products, setProducts] = useState([]);
  const [predivedProducts, setPredivedProducts] = useState([]);
  const [trips, setTrips] = useState([]);
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [viewingActivity, setViewingActivity] = useState(null);
  const [activityFilter, setActivityFilter] = useState('');
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPredivedModal, setShowPredivedModal] = useState(false);
  const [showTripModal, setShowTripModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // Bulk-edit state
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkPriceMode, setBulkPriceMode] = useState('percent'); // 'percent' | 'flat' | 'set'
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  const [bulkPriceTarget, setBulkPriceTarget] = useState('price'); // 'price' | 'cost_price'
  // Featured rotation settings
  const [featuredSettings, setFeaturedSettings] = useState({ enabled: true, last_run_at: null });
  const [featuredBusy, setFeaturedBusy] = useState(false);

  const fetchFeaturedSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/featured/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setFeaturedSettings(res.data);
    } catch (_) { /* non-critical */ }
  };

  const toggleFeaturedAuto = async (enabled) => {
    setFeaturedBusy(true);
    try {
      const res = await axios.patch(
        `${API_URL}/admin/featured/settings`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeaturedSettings(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.message);
    } catch (e) {
      toast.error('Failed to update setting');
    } finally {
      setFeaturedBusy(false);
    }
  };

  const randomizeFeaturedNow = async () => {
    if (!window.confirm('Randomise the featured products NOW? This unfeatures all current featured items and picks 5 new ones in New Gear and 5 in Pre-Dived.')) return;
    setFeaturedBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/featured/randomize`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      toast.success(`${res.data.message} — ${res.data.new_gear_count} New Gear, ${res.data.predived_count} Pre-Dived`);
      fetchData();
      fetchFeaturedSettings();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Randomise failed');
    } finally {
      setFeaturedBusy(false);
    }
  };
  const [editingPredived, setEditingPredived] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [viewBookingsTrip, setViewBookingsTrip] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({
    name: '', description: '', category: 'masks', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false, currency: 'ZAR', supplier_id: null
  });
  const [predivedForm, setPredivedForm] = useState({
    name: '', description: '', subcategory: 'bcds', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false, currency: 'ZAR', supplier_id: null
  });
  const [tripForm, setTripForm] = useState({
    destination: '', destinations_text: '', description: '', start_date: '', end_date: '', price: '', image_url: '', max_participants: 12, available_on_pro: false,
    dive_site: '', sighting_possibilities: '', typical_weather: '', area_activities: '',
    accommodation_options: [], inclusions: '', self_catering: false, catered: false, catered_price: 0, nutshell: '', inquire_only: false, show_starting_from: false, currency: 'ZAR', visible: true, supplier_id: null
  });
  const [courseForm, setCourseForm] = useState({
    name: '', description: '', price: '', level: 'Beginner', category: 'beginner', image_url: '', in_stock: true,
    min_age: '', what_to_expect: '', career_opportunities: '', fun_fact: '', currency: 'ZAR', supplier_id: null
  });
  const [eventForm, setEventForm] = useState({
    name: '', description: '', event_date: '', end_date: '', event_time: '', price: '', price_starting_from: false, image_url: '', location: '', max_participants: 50, in_stock: true,
    what_to_expect: '', typical_weather: '', highlights: '', does_not_include: '', sessions: [], supplier_id: null
  });
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '', first_name: '', last_name: '', contact_number: '', physical_address: '', password: '', membership_tier: '', custom_discount: ''
  });
  const [siteContent, setSiteContent] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (activeTab === 'dashboard') {
        const [analyticsRes, ordersRes, autoRes] = await Promise.all([
          axios.get(`${API_URL}/admin/analytics`, { headers }),
          axios.get(`${API_URL}/admin/orders`, { headers }),
          axios.get(`${API_URL}/admin/ab-tests/dashboard-stats`, { headers }).catch(() => ({ data: null }))
        ]);
        setAnalytics(analyticsRes.data);
        setOrders(ordersRes.data.slice(0, 5)); // Recent 5 orders
        setAutomationStats(autoRes.data);
      } else if (activeTab === 'orders') {
        const res = await axios.get(`${API_URL}/admin/orders`, { headers });
        setOrders(res.data);
      } else if (activeTab === 'members') {
        const res = await axios.get(`${API_URL}/admin/members`, { headers });
        setMembers(res.data);
      } else if (activeTab === 'products') {
        const [prodRes] = await Promise.all([
          axios.get(`${API_URL}/admin/products`, { headers }),
          fetchFeaturedSettings(),
        ]);
        // Filter out pre-owned products
        setProducts(prodRes.data.filter(p => p.category !== 'pre-owned'));
      } else if (activeTab === 'predived') {
        const res = await axios.get(`${API_URL}/products/pre-owned`, { headers });
        setPredivedProducts(res.data);
      } else if (activeTab === 'trips') {
        const res = await axios.get(`${API_URL}/admin/trips`, { headers });
        setTrips(res.data);
      } else if (activeTab === 'courses') {
        const res = await axios.get(`${API_URL}/admin/courses`, { headers });
        setCourses(res.data);
      } else if (activeTab === 'events') {
        const res = await axios.get(`${API_URL}/admin/events`, { headers });
        setEvents(res.data);
      } else if (activeTab === 'settings') {
        const res = await axios.get(`${API_URL}/admin/site-content`, { headers });
        setSiteContent(res.data);
      } else if (activeTab === 'activity') {
        const res = await axios.get(`${API_URL}/admin/analytics/views`, { headers });
        setViewingActivity(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, field, value) => {
    try {
      const updateData = field === 'payment' ? { payment_status: value } : { order_status: value };
      await axios.patch(`${API_URL}/admin/orders/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const cancelOrderAndCredit = async (order) => {
    const issueCredit = window.confirm(
      `Cancel order ${order.order_number || order.id?.slice(0, 8)} (R${(order.total || 0).toFixed(2)})?\n\n` +
      `Press OK to issue R${(order.total || 0).toFixed(2)} as STORE CREDIT to the customer.\n` +
      `Press Cancel to skip the credit (just mark cancelled).`
    );
    // If user dismissed entirely (Esc), abort. Browser's window.confirm only
    // returns boolean — we use a second prompt to confirm the cancel action
    // itself when they decline the credit.
    if (!issueCredit) {
      if (!window.confirm(`Mark order ${order.order_number || ''} as cancelled WITHOUT issuing credit?`)) return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/admin/orders/${order.id}/cancel`,
        { issue_credit: issueCredit, notify_customer: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.credit_issued) {
        toast.success(`Cancelled · R${res.data.credit.value.toFixed(2)} store credit issued`);
      } else {
        toast.success('Order cancelled');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const updateMembership = async (userId, tier) => {
    try {
      await axios.patch(`${API_URL}/admin/members/${userId}`, 
        { membership_tier: tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tier ? `Membership activated: ${tier.toUpperCase()}` : 'Membership removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update membership');
    }
  };

  const handleProductSubmit = async () => {
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        cost_price: productForm.cost_price ? parseFloat(productForm.cost_price) : null,
        sizes: productForm.sizes?.length > 0 ? productForm.sizes : null,
        size_type: productForm.size_type || null,
        available_colors: productForm.available_colors?.length > 0 ? productForm.available_colors : null,
        custom_colors: productForm.custom_colors?.length > 0 ? productForm.custom_colors : null,
        order: parseInt(productForm.order) || 0
      };
      
      if (editingProduct) {
        await axios.patch(`${API_URL}/admin/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }
      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', category: 'masks', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false, currency: 'ZAR', supplier_id: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handlePredivedSubmit = async () => {
    try {
      // Map subcategory to description prefix
      const subcategoryDescriptions = {
        'curated-sets': 'Pre-dived gear set',
        'cylinders': 'Pre-dived cylinder',
        bcds: 'Pre-dived BCD',
        regulators: 'Pre-dived regulator set',
        wetsuits: 'Pre-dived wetsuit',
        fins: 'Pre-dived fins',
        masks: 'Pre-dived mask',
        computers: 'Pre-dived dive computer',
        accessories: 'Pre-dived accessory'
      };
      
      const descPrefix = subcategoryDescriptions[predivedForm.subcategory] || 'Pre-dived gear';
      const sizeText = predivedForm.sizes ? ` Size ${predivedForm.sizes}.` : '';
      const description = predivedForm.description || `${descPrefix} in good condition.${sizeText}`;
      
      const data = {
        name: predivedForm.name,
        description: description,
        category: 'pre-owned',
        subcategory: predivedForm.subcategory,
        price: parseFloat(predivedForm.price),
        cost_price: predivedForm.cost_price ? parseFloat(predivedForm.cost_price) : null,
        image_url: predivedForm.image_url,
        in_stock: predivedForm.in_stock,
        sizes: predivedForm.sizes?.length > 0 ? predivedForm.sizes : null,
        size_type: predivedForm.size_type || null,
        available_colors: predivedForm.available_colors?.length > 0 ? predivedForm.available_colors : null,
        custom_colors: predivedForm.custom_colors?.length > 0 ? predivedForm.custom_colors : null,
        order: parseInt(predivedForm.order) || 0,
        price_on_request: predivedForm.price_on_request || false
      };
      
      if (editingPredived) {
        await axios.patch(`${API_URL}/admin/products/${editingPredived.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pre-dived item updated');
      } else {
        await axios.post(`${API_URL}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pre-dived item created');
      }
      setShowPredivedModal(false);
      setEditingPredived(null);
      setPredivedForm({ name: '', description: '', subcategory: 'bcds', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to save pre-dived item');
    }
  };

  const deletePredived = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pre-dived item?')) return;
    try {
      await axios.delete(`${API_URL}/admin/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pre-dived item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pre-dived item');
    }
  };

  const handleTripSubmit = async () => {
    try {
      // Sanitize accommodation options
      const accomm = (tripForm.accommodation_options || [])
        .map((o) => ({
          name: (o.name || '').trim(),
          price: parseFloat(o.price),
          description: (o.description || '').trim() || null,
          pricing_basis: o.pricing_basis || 'pps',
        }))
        .filter((o) => o.name && !isNaN(o.price) && o.price > 0);

      // Inquire-only trips don't require firm dates/price/max participants — backfill placeholders
      // so the model validation passes. The frontend hides these fields when inquire_only is on.
      const isInquireOnly = !!tripForm.inquire_only;
      const startDate = isInquireOnly && !tripForm.start_date ? 'TBC' : tripForm.start_date;
      const endDate = isInquireOnly && !tripForm.end_date ? 'TBC' : tripForm.end_date;
      const priceNum = parseFloat(tripForm.price);
      const maxNum = parseInt(tripForm.max_participants);

      const data = {
        ...tripForm,
        start_date: startDate,
        end_date: endDate,
        price: !isNaN(priceNum) ? priceNum : (isInquireOnly ? 0 : 0),
        max_participants: !isNaN(maxNum) ? maxNum : (isInquireOnly ? 12 : 12),
        available_on_pro: tripForm.available_on_pro,
        inquire_only: isInquireOnly,
        visible: tripForm.visible !== false,
        accommodation_options: accomm.length > 0 ? accomm : null,
        // Split the raw textarea contents at save time (splitting per keystroke
        // would eat any space the admin tried to type mid-word). Only send
        // `destinations` when 2+ non-empty lines are present.
        destinations: (() => {
          const lines = (tripForm.destinations_text || '')
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          return lines.length >= 2 ? lines : null;
        })(),
      };
      // Never send the raw textarea buffer to the API — the backend model
      // only knows about `destinations`.
      delete data.destinations_text;

      if (editingTrip) {
        await axios.patch(`${API_URL}/admin/trips/${editingTrip.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Trip updated');
      } else {
        await axios.post(`${API_URL}/admin/trips`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Trip created');
      }
      setShowTripModal(false);
      setEditingTrip(null);
      setTripForm({ destination: '', destinations_text: '', description: '', start_date: '', end_date: '', price: '', image_url: '', max_participants: 12, available_on_pro: false, dive_site: '', sighting_possibilities: '', typical_weather: '', area_activities: '', accommodation_options: [], inclusions: '', self_catering: false, catered: false, catered_price: 0, nutshell: '', inquire_only: false, show_starting_from: false, currency: 'ZAR', visible: true });
      fetchData();
    } catch (error) {
      toast.error('Failed to save trip');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/admin/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleFeatured = async (productId) => {
    try {
      const res = await axios.patch(`${API_URL}/admin/products/${productId}/featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, featured: res.data.featured } : p));
      setPredivedProducts(prev => prev.map(p => p.id === productId ? { ...p, featured: res.data.featured } : p));
      toast.success(res.data.featured ? 'Added to Featured' : 'Removed from Featured');
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const deleteTrip = async (id) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;
    try {
      await axios.delete(`${API_URL}/admin/trips/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  };

  const handleCourseSubmit = async () => {
    try {
      const data = {
        ...courseForm,
        price: parseFloat(courseForm.price),
      };
      
      if (editingCourse) {
        await axios.patch(`${API_URL}/admin/courses/${editingCourse.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course updated');
      } else {
        await axios.post(`${API_URL}/admin/courses`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course created');
      }
      setShowCourseModal(false);
      setEditingCourse(null);
      setCourseForm({ name: '', description: '', price: '', level: 'Beginner', category: 'beginner', image_url: '', in_stock: true, min_age: '', what_to_expect: '', career_opportunities: '', fun_fact: '', currency: 'ZAR' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await axios.delete(`${API_URL}/admin/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleEventSubmit = async () => {
    try {
      const cleanedSessions = (eventForm.sessions || [])
        .filter(s => s && (s.title || s.date))
        .map(s => ({
          title: s.title || '',
          date: s.date || '',
          time: s.time || null,
          end_date: s.end_date || null,
          price: s.price !== '' && s.price !== null && s.price !== undefined ? parseFloat(s.price) : null,
        }));
      const data = {
        ...eventForm,
        price: parseFloat(eventForm.price),
        max_participants: parseInt(eventForm.max_participants) || 50,
        sessions: cleanedSessions,
        // Once admin saves with in_stock=true (active), clear the draft flag.
        is_draft: eventForm.in_stock ? false : (editingEvent?.is_draft || false),
      };
      
      if (editingEvent) {
        await axios.patch(`${API_URL}/admin/events/${editingEvent.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated');
      } else {
        await axios.post(`${API_URL}/admin/events`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created');
      }
      setShowEventModal(false);
      setEditingEvent(null);
      setEventForm({ name: '', description: '', event_date: '', end_date: '', event_time: '', price: '', price_starting_from: false, image_url: '', location: '', max_participants: 50, in_stock: true, what_to_expect: '', typical_weather: '', highlights: '', does_not_include: '', sessions: [], supplier_id: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/admin/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');    }
  };

  const duplicateEvent = async (event) => {
    if (!window.confirm(`Duplicate "${event.name}"? The copy will be created as a Draft (hidden from the public events page) for you to edit.`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/events/${event.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event duplicated — opening the copy for editing');
      const copy = res.data;
      await fetchData();
      // Open the duplicated event immediately in edit mode
      setEditingEvent(copy);
      setEventForm({
        name: copy.name || '',
        description: copy.description || '',
        event_date: copy.event_date || '',
        end_date: copy.end_date || '',
        event_time: copy.event_time || '',
        price: copy.price?.toString?.() || '',
        price_starting_from: !!copy.price_starting_from,
        image_url: copy.image_url || '',
        location: copy.location || '',
        max_participants: copy.max_participants || 50,
        in_stock: copy.in_stock,
        what_to_expect: copy.what_to_expect || '',
        typical_weather: copy.typical_weather || '',
        highlights: copy.highlights || '',
        does_not_include: copy.does_not_include || '',
        sessions: (copy.sessions || []).map(s => ({
          title: s.title || '',
          date: s.date || '',
          time: s.time || '',
          end_date: s.end_date || '',
          price: s.price !== null && s.price !== undefined ? s.price.toString() : '',
        })),
        supplier_id: copy.supplier_id || null,
      });
      setShowEventModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate event');
    }
  };

  const deleteMember = async (id, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleGrantPromo = async () => {
    if (selectedMemberIds.size === 0) { toast.error('Select at least one user'); return; }
    let value, applies_to_category = null;
    if (promoForm.type === 'free_item') {
      if (!promoForm.free_item_id) { toast.error('Choose an item to make free'); return; }
      value = promoForm.free_item_id;
    } else {
      const amt = parseFloat(promoForm.amount);
      if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
      value = amt;
      applies_to_category = promoForm.applies_to_category || null;
    }
    try {
      const body = {
        user_ids: Array.from(selectedMemberIds),
        type: promoForm.type,
        value,
        applies_to_category,
        expires_at: promoForm.expires_at ? new Date(promoForm.expires_at + 'T23:59:59').toISOString() : null,
        reason: promoForm.reason || (promoForm.type === 'free_item' ? `Free: ${promoForm.free_item_label}` : `R${value} off`),
      };
      const res = await axios.post(`${API_URL}/admin/promo-credits/grant`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Granted to ${res.data.granted} user${res.data.granted !== 1 ? 's' : ''}`);
      setShowPromoModal(false);
      setSelectedMemberIds(new Set());
      setPromoForm({ type: 'free_item', free_item_id: '', free_item_label: '', amount: '', applies_to_category: '', expires_at: '', reason: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant promo');
    }
  };

  const openApplyPromoToOrder = async (order) => {
    setApplyPromoOrder(order);
    setSelectedCreditId('');
    setForceApplyEnabled(false);
    setForceItemIndex(null);
    try {
      // Fetch this user's unused credits
      const res = await axios.get(`${API_URL}/admin/promo-credits?user_id=${order.user_id}&include_used=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const unused = (res.data || []).filter(c => !c.used_at);
      setApplyPromoCredits(unused);
      if (unused.length === 0) {
        toast.error('This user has no unused promo credits. Grant one in Members tab first.');
        return;
      }
    } catch (e) {
      toast.error('Failed to load user credits');
      return;
    }
  };

  const confirmApplyPromoToOrder = async () => {
    if (!applyPromoOrder || !selectedCreditId) {
      toast.error('Pick a credit to apply');
      return;
    }
    try {
      const payload = { credit_id: selectedCreditId, refund_required: true };
      if (forceApplyEnabled) {
        if (forceItemIndex === null || forceItemIndex === undefined) {
          toast.error('Pick which line item to apply the credit to');
          return;
        }
        payload.force_item_index = forceItemIndex;
      }
      const res = await axios.post(
        `${API_URL}/admin/promo-credits/apply-to-order/${applyPromoOrder.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Applied! New total R${res.data.new_total}. ${res.data.refund_amount_due > 0 ? `Refund due to customer: R${res.data.refund_amount_due.toFixed(2)}.` : ''}`,
        { duration: 6000 }
      );
      setApplyPromoOrder(null);
      setApplyPromoCredits([]);
      setSelectedCreditId('');
      setForceApplyEnabled(false);
      setForceItemIndex(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply credit');
    }
  };

  const openGrantPromoModal = async () => {
    // Lazily fetch courses + events if they haven't loaded yet (the Members tab
    // doesn't fetch them by default, so the "Choose item" dropdown was empty).
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [cRes, eRes] = await Promise.all([
        axios.get(`${API_URL}/admin/courses`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/admin/events`, { headers }).catch(() => ({ data: null })),
      ]);
      if (Array.isArray(cRes.data) && cRes.data.length > 0) setCourses(cRes.data);
      if (Array.isArray(eRes.data) && eRes.data.length > 0) setEvents(eRes.data);
    } catch (_) { /* ignore */ }
    setShowPromoModal(true);
  };

  const handleUserSubmit = async () => {
    try {
      const data = {
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        contact_number: userForm.contact_number,
        physical_address: userForm.physical_address,
        membership_tier: userForm.membership_tier || null,
        custom_discount: userForm.custom_discount ? parseFloat(userForm.custom_discount) : 0,
      };
      
      if (editingUser) {
        // Update existing user
        if (userForm.password) {
          data.password = userForm.password; // Include password only if provided
        }
        // Allow admin to fix an incorrectly-entered email address.
        if (userForm.email && userForm.email.toLowerCase() !== (editingUser.email || '').toLowerCase()) {
          data.email = userForm.email;
        }
        await axios.put(`${API_URL}/admin/members/${editingUser.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated');
      } else {
        // Create new user
        if (!userForm.email || !userForm.password) {
          toast.error('Email and password are required');
          return;
        }
        await axios.post(`${API_URL}/admin/members`, {
          ...data,
          email: userForm.email,
          password: userForm.password,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User created');
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ email: '', first_name: '', last_name: '', contact_number: '', physical_address: '', password: '', membership_tier: '', custom_discount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const updates = Object.entries(siteContent).map(([key, value]) => ({ key, value }));
      await axios.put(`${API_URL}/admin/site-content/bulk`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleBackup = async () => {
    try {
      await axios.post(`${API_URL}/admin/send-backup-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup email sent to byron@justdive.co.za');
    } catch (error) {
      toast.error('Failed to send backup');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'homepage', label: 'Homepage', icon: Eye },
    { id: 'popups', label: 'Popups', icon: Megaphone },
    { id: 'memberships', label: 'Memberships', icon: Crown },
    { id: 'page-headers', label: 'Page Headers', icon: Eye },
    { id: 'planner-ops', label: 'Planner Ops', icon: Anchor },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'supplier-invoices', label: 'Supplier Invoices', icon: FileText },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'catalog', label: 'Catalog Sync', icon: RefreshCw },
    { id: 'automation', label: 'Automation', icon: Activity },
    { id: 'predived', label: 'Pre-Dived', icon: Package },
    { id: 'trips', label: 'Trips', icon: MapPin },
    { id: 'trip-inquiries', label: 'Trip Inquiries', icon: Mail },
    { id: 'courses', label: 'Courses', icon: GraduationCap },
    { id: 'events', label: 'Events', icon: Sparkles },
    { id: 'brands', label: 'Brands', icon: Crown },
    { id: 'suppliers', label: 'Suppliers', icon: Truck },
    { id: 'promos', label: 'Promos', icon: Gift },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'forms', label: 'Forms', icon: ClipboardList },
    { id: 'communications', label: 'Communications', icon: Mail },
    { id: 'newsletters', label: 'Newsletter', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'activity', label: 'Activity', icon: Activity },
  ];

  // Get item category for display
  const getItemCategory = (item) => {
    // Handle brand products (like DiveVolk)
    if (item.type === 'brand_product') {
      return item.brand_name || 'DiveVolk';
    }
    if (item.category === 'pre-owned') return 'Pre-Dived';
    const categories = {
      'masks': 'Masks', 'fins': 'Fins', 'bcds': 'BCDs', 'regulators': 'Regulators',
      'computers': 'Dive Computers', 'wetsuits': 'Wetsuits', 'accessories': 'Accessories',
      'membership': 'Membership', 'sets': 'Sets', 'torches': 'Torches'
    };
    return categories[item.category] || item.category || 'Products';
  };

  // Filter data based on search query
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      order.id.toLowerCase().includes(q) ||
      order.customer_info?.name?.toLowerCase().includes(q) ||
      order.customer_info?.email?.toLowerCase().includes(q) ||
      order.items?.some(item => item.name?.toLowerCase().includes(q))
    );
  });

  const filteredMembers = members.filter(member => {
    // Date range filter — uses created_at or date_joined (whichever is present)
    const joinedRaw = member.created_at || member.date_joined;
    if (memberDateFrom && joinedRaw) {
      if (new Date(joinedRaw) < new Date(memberDateFrom)) return false;
    }
    if (memberDateTo && joinedRaw) {
      const end = new Date(memberDateTo);
      end.setHours(23, 59, 59);
      if (new Date(joinedRaw) > end) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      member.first_name?.toLowerCase().includes(q) ||
      member.last_name?.toLowerCase().includes(q) ||
      member.email?.toLowerCase().includes(q) ||
      member.contact_number?.includes(q) ||
      member.membership_number?.toLowerCase().includes(q)
    );
  });

  const filteredProducts = products.filter(product => {
    // Category filter
    if (productCategoryFilter !== 'all' && product.category !== productCategoryFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      product.name?.toLowerCase().includes(q) ||
      product.description?.toLowerCase().includes(q) ||
      product.category?.toLowerCase().includes(q)
    );
  });

  // Distinct categories (excluding pre-owned which lives in its own tab) for the reorder filter
  const distinctProductCategories = Array.from(
    new Set(products.filter(p => p.category !== 'pre-owned').map(p => p.category).filter(Boolean))
  ).sort();

  // --- Bulk edit handlers ---
  const toggleSelectProduct = (id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllFiltered = (rows = null) => {
    // If a specific row list is passed (e.g. `filteredPredived`), use it —
    // otherwise fall back to `filteredProducts` (new gear tab).
    const source = rows || filteredProducts;
    const allIds = new Set(source.map(p => p.id));
    const allSelected = source.length > 0 && source.every(p => selectedProductIds.has(p.id));
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach(id => next.delete(id));
      } else {
        allIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedProductIds(new Set());

  const callBulkUpdate = async (body) => {
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/products/bulk-update`,
        { product_ids: Array.from(selectedProductIds), ...body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Bulk update applied');
      fetchData();
      clearSelection();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkCategory = () => {
    if (!bulkCategory) { toast.error('Pick a category first'); return; }
    if (!window.confirm(`Change category to "${bulkCategory}" for ${selectedProductIds.size} product(s)?`)) return;
    callBulkUpdate({ category: bulkCategory });
  };
  const handleBulkInStock = (in_stock) => {
    if (!window.confirm(`Mark ${selectedProductIds.size} product(s) as ${in_stock ? 'In Stock' : 'Out of Stock'}?`)) return;
    callBulkUpdate({ in_stock });
  };
  const handleBulkPrice = () => {
    const val = parseFloat(bulkPriceValue);
    if (isNaN(val)) { toast.error('Enter a number for the price change'); return; }
    let body = {};
    const field = bulkPriceTarget === 'cost_price' ? 'cost_price' : 'price';
    if (bulkPriceMode === 'percent') {
      body[`${field}_change_percent`] = val;
    } else if (bulkPriceMode === 'flat') {
      body[`${field}_change_flat`] = val;
    } else if (bulkPriceMode === 'set') {
      if (bulkPriceTarget !== 'price') { toast.error('"Set" mode only supports the Sell Price'); return; }
      body['price_set'] = val;
    } else if (bulkPriceMode === 'markup') {
      if (bulkPriceTarget !== 'price') { toast.error('"Set markup" recalculates the Sell Price from Cost — switch target to "Sell"'); return; }
      body['set_markup_percent'] = val;
    }
    let verb;
    if (bulkPriceMode === 'percent') verb = `${val > 0 ? '+' : ''}${val}%`;
    else if (bulkPriceMode === 'flat') verb = `R${val > 0 ? '+' : ''}${val}`;
    else if (bulkPriceMode === 'set') verb = `set to R${val}`;
    else verb = `set markup to ${val}% (price = cost × ${(1 + val / 100).toFixed(2)})`;
    if (!window.confirm(`Apply ${verb} to ${selectedProductIds.size} product(s)?`)) return;
    callBulkUpdate(body);
  };

  const filteredPredived = predivedProducts.filter(product => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      product.name?.toLowerCase().includes(q) ||
      product.description?.toLowerCase().includes(q) ||
      product.subcategory?.toLowerCase().includes(q)
    );
  });

  const filteredTrips = trips.filter(trip => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      trip.destination?.toLowerCase().includes(q) ||
      trip.description?.toLowerCase().includes(q)
    );
  });

  const filteredCourses = courses.filter(course => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      course.name?.toLowerCase().includes(q) ||
      course.description?.toLowerCase().includes(q) ||
      course.level?.toLowerCase().includes(q)
    );
  });

  const filteredEvents = events.filter(event => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      event.name?.toLowerCase().includes(q) ||
      event.description?.toLowerCase().includes(q) ||
      event.location?.toLowerCase().includes(q)
    );
  });

  // Export members to CSV
  const exportMembersCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Contact Number', 'Address', 'Membership Tier', 'Membership Number'];
    const rows = members.map(m => [
      m.first_name || '',
      m.last_name || '',
      m.email || '',
      m.contact_number || '',
      (m.physical_address || '').replace(/,/g, ' '),
      m.membership_tier || 'None',
      m.membership_number || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `justdive_members_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Members exported successfully');
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-[#0A2342] text-white py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-300 text-xs md:text-sm">Manage your Just Dive store</p>
            </div>
            <Link to="/planner" className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-3 py-2 rounded-lg text-sm transition w-full md:w-auto justify-center" data-testid="admin-planner-link">
              <Anchor className="w-4 h-4" />
              <span>Open Dive Planner</span>
            </Link>
            {/* Search Bar */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/20 text-sm"
                data-testid="admin-search"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-2 md:px-6">
          <div className="flex gap-0 md:gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-600 hover:text-[#0A2342]'
                }`}
              >
                <tab.icon className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.slice(0, 6)}{tab.label.length > 6 ? '' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <ErrorBoundary>
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && analytics && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                title="Total Revenue" 
                value={`R${analytics.revenue.total_paid.toLocaleString()}`}
                subtitle={`R${analytics.revenue.pending.toLocaleString()} pending`}
                icon={TrendingUp}
                color="green"
                onClick={() => setActiveTab('invoices')}
                testId="dashboard-tile-revenue"
              />
              <StatCard 
                title="Total Orders" 
                value={analytics.orders.total}
                subtitle={`${analytics.orders.pending_payment} pending payment`}
                icon={ShoppingBag}
                color="blue"
                onClick={() => setActiveTab('orders')}
                testId="dashboard-tile-orders"
              />
              <StatCard 
                title="Members" 
                value={analytics.members.club_members + analytics.members.pro_members}
                subtitle={`${analytics.members.pro_members} Pro, ${analytics.members.club_members} Club`}
                icon={Crown}
                color="purple"
                onClick={() => setActiveTab('members')}
                testId="dashboard-tile-members"
              />
              <StatCard 
                title="Products" 
                value={analytics.products.total}
                subtitle={`${analytics.products.out_of_stock} out of stock`}
                icon={Package}
                color="orange"
                onClick={() => setActiveTab('products')}
                testId="dashboard-tile-products"
              />
            </div>

            {/* Daily Sign-ups + Active Promos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Sign-ups Today"
                value={analytics.members.today_signups ?? 0}
                subtitle={`${(analytics.members.daily_signups || []).reduce((s, d) => s + d.signups, 0)} in last 7 days`}
                icon={Users}
                color="green"
                onClick={() => setActiveTab('members')}
                testId="dashboard-tile-signups"
              />
              <StatCard
                title="Total Users"
                value={analytics.members.total_users}
                subtitle={`${analytics.members.non_members} non-members`}
                icon={Users}
                color="blue"
                onClick={() => setActiveTab('members')}
                testId="dashboard-tile-total-users"
              />
              <StatCard
                title="Active Promos"
                value={analytics.promos?.active_total ?? 0}
                subtitle={`${(analytics.promos?.recent_claims || []).length} recent claims`}
                icon={Gift}
                color="orange"
                onClick={() => setActiveTab('promos')}
                testId="dashboard-tile-active-promos"
              />
              <StatCard
                title="Promo Claims Today"
                value={(analytics.promos?.recent_claims || []).filter(c => c.used_at && c.used_at.startsWith(new Date().toISOString().slice(0,10))).length}
                subtitle="Redeemed by customers"
                icon={Gift}
                color="purple"
                onClick={() => setActiveTab('promos')}
                testId="dashboard-tile-promo-claims"
              />
            </div>

            {/* Page Visits Stats */}
            {analytics.visits && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard 
                  title="Page Visits Today" 
                  value={analytics.visits.today}
                  subtitle={`${analytics.visits.total.toLocaleString()} total visits`}
                  icon={Eye}
                  color="cyan"
                  onClick={() => setActiveTab('activity')}
                  testId="dashboard-tile-page-visits"
                />
                <StatCard 
                  title="Site Activity" 
                  value={analytics.visits.daily?.reduce((sum, d) => sum + d.visits, 0) || 0}
                  subtitle="Last 7 days"
                  icon={Activity}
                  color="indigo"
                  onClick={() => setActiveTab('activity')}
                  testId="dashboard-tile-site-activity"
                />
              </div>
            )}

            {/* Visits Chart - Last 7 Days */}
            {analytics.visits?.daily && analytics.visits.daily.length > 0 && (
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-lg font-semibold mb-4">Page Visits - Last 7 Days</h2>
                <div className="flex items-end justify-between h-40 gap-2">
                  {analytics.visits.daily.map((day, idx) => {
                    const maxVisits = Math.max(...analytics.visits.daily.map(d => d.visits), 1);
                    const height = (day.visits / maxVisits) * 100;
                    return (
                      <div key={idx} className="flex flex-col items-center flex-1">
                        <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '120px' }}>
                          <div 
                            className="absolute bottom-0 w-full bg-[#0A2342] rounded-t transition-all"
                            style={{ height: `${height}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 mt-2">
                          {new Date(day.date).toLocaleDateString('en-ZA', { weekday: 'short' })}
                        </span>
                        <span className="text-xs font-semibold">{day.visits}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Daily Sign-ups Chart - Last 7 Days */}
            {analytics.members.daily_signups && analytics.members.daily_signups.length > 0 && (
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Daily Sign-ups - Last 7 Days
                </h2>
                <div className="flex items-end justify-between h-40 gap-2">
                  {analytics.members.daily_signups.map((day, idx) => {
                    const maxSignups = Math.max(...analytics.members.daily_signups.map(d => d.signups), 1);
                    const height = (day.signups / maxSignups) * 100;
                    return (
                      <div key={idx} className="flex flex-col items-center flex-1">
                        <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '120px' }}>
                          <div
                            className="absolute bottom-0 w-full bg-green-600 rounded-t transition-all"
                            style={{ height: `${height}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 mt-2">
                          {new Date(day.date).toLocaleDateString('en-ZA', { weekday: 'short' })}
                        </span>
                        <span className="text-xs font-semibold">{day.signups}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active Promos + Recent Claims */}
            {(analytics.promos?.active_summary?.length > 0 || analytics.promos?.recent_claims?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Active promos breakdown */}
                <div className="bg-white rounded-lg border p-6" data-testid="active-promos-panel">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#FF6B35]" />
                    Active Promos ({analytics.promos.active_total})
                  </h2>
                  {(!analytics.promos.active_summary || analytics.promos.active_summary.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">No active promo credits.</p>
                  ) : (
                    <div className="space-y-3">
                      {analytics.promos.active_summary.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between border rounded p-3 bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#0A2342]">
                              {p.type === 'free_item' ? '🎁 Free Item' : `R${Number(p.value).toLocaleString()} Off`}
                              {p.applies_to_category && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{p.applies_to_category}</span>}
                            </div>
                            {p.reason && <div className="text-xs text-gray-600 truncate">{p.reason}</div>}
                          </div>
                          <div className="ml-3 text-right">
                            <div className="text-xl font-bold text-[#FF6B35]">{p.count}</div>
                            <div className="text-xs text-gray-500">user{p.count !== 1 ? 's' : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent claims audit */}
                <div className="bg-white rounded-lg border p-6" data-testid="promo-claims-panel">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    Recent Promo Claims
                  </h2>
                  {(!analytics.promos.recent_claims || analytics.promos.recent_claims.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">No claims yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {analytics.promos.recent_claims.map((c, idx) => (
                        <div key={c.id || idx} className="border rounded p-3 bg-gray-50 text-sm" data-testid={`promo-claim-row-${idx}`}>
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-[#0A2342] truncate">{c.user_email}</div>
                              <div className="text-xs text-gray-600">
                                {c.type === 'free_item' ? '🎁 Free item' : `R${Number(c.value).toLocaleString()} off`}
                                {c.applies_to_category && ` (${c.applies_to_category})`}
                              </div>
                              {c.reason && <div className="text-xs text-gray-500 italic truncate">{c.reason}</div>}
                            </div>
                            <div className="text-right whitespace-nowrap">
                              <div className="text-sm font-semibold text-green-700">−R{Number(c.discount_value || 0).toLocaleString()}</div>
                              <div className="text-xs text-gray-500">
                                {c.used_at ? new Date(c.used_at).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Browse Automation Widget */}
            {automationStats && (
              <div className="bg-white rounded-lg border p-6" data-testid="automation-dashboard-widget">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Browse Automation</h2>
                  <button onClick={() => setActiveTab('automation')} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                    View details <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-700">{automationStats.emails_this_week}</div>
                    <div className="text-xs text-blue-600">Emails This Week</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-700">{automationStats.open_rate}%</div>
                    <div className="text-xs text-green-600">Open Rate</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-xl font-bold text-yellow-700">{automationStats.click_rate}%</div>
                    <div className="text-xs text-yellow-600">Click Rate</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-xl font-bold text-purple-700">{automationStats.conversion_rate}%</div>
                    <div className="text-xs text-purple-600">Conversion Rate</div>
                  </div>
                  <div className={`text-center p-3 rounded-lg ${automationStats.is_payday_window ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <div className="text-xl font-bold">{automationStats.is_payday_window ? 'Active' : 'Off'}</div>
                    <div className="text-xs text-gray-600">Payday Window</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{automationStats.active_sessions} active sessions &bull; {automationStats.queue_pending} queued</span>
                  {automationStats.best_variant && (
                    <span>Best CTA: <strong className="text-[#0A2342]">"{automationStats.best_variant.cta}"</strong> ({automationStats.best_variant.click_rate}% clicks)</span>
                  )}
                  {automationStats.last_rotation && (
                    <span>Last rotation: {new Date(automationStats.last_rotation).toLocaleDateString('en-GB')}</span>
                  )}
                </div>
              </div>
            )}

            {/* Two Column Layout for Top Pages and Products */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Pages */}
              {analytics.visits?.by_page && analytics.visits.by_page.length > 0 && (
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="text-lg font-semibold mb-4">Top Pages</h2>
                  <div className="space-y-3">
                    {analytics.visits.by_page.slice(0, 5).map((page, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm font-mono truncate max-w-[200px]">{page._id}</span>
                        <Badge variant="outline">{page.count} visits</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Viewed Products */}
              {analytics.visits?.top_products && analytics.visits.top_products.length > 0 && (
                <div className="bg-white rounded-lg border p-6">
                  <h2 className="text-lg font-semibold mb-4">Most Viewed Products</h2>
                  <div className="space-y-3">
                    {analytics.visits.top_products.slice(0, 5).map((product, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block">{product.name}</span>
                          <span className="text-xs text-gray-500">{product.category}</span>
                        </div>
                        <Badge className="bg-[#FF6B35] text-white ml-2">{product.view_count} views</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold mb-4">Recent Orders</h2>
              <div className="space-y-3">
                {orders.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No orders yet</p>
                ) : orders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-mono text-sm font-semibold">{order.id.slice(0, 8).toUpperCase()}</span>
                      <span className="text-gray-500 mx-2">•</span>
                      <span className="text-sm text-gray-600">{order.customer_info?.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">R{order.total.toFixed(0)}</span>
                      <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              {orders.length > 0 && (
                <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('orders')}>
                  View All Orders
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Orders ({filteredOrders.length}{searchQuery ? ` of ${orders.length}` : ''})</h2>
              <Button onClick={() => setShowManualOrderModal(true)} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="open-manual-order-btn">
                <Plus className="w-4 h-4 mr-2" /> New Manual Order
              </Button>
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0A2342] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm">Order #</th>
                    <th className="px-4 py-3 text-left text-sm">Customer</th>
                    <th className="px-4 py-3 text-left text-sm">Items</th>
                    <th className="px-4 py-3 text-left text-sm">Source</th>
                    <th className="px-4 py-3 text-right text-sm">Total</th>
                    <th className="px-4 py-3 text-center text-sm">Payment</th>
                    <th className="px-4 py-3 text-center text-sm">Status</th>
                    <th className="px-4 py-3 text-center text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        {searchQuery ? 'No orders match your search' : 'No orders yet'}
                      </td>
                    </tr>
                  ) : filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-semibold">{order.id.slice(0, 8).toUpperCase()}</span>
                        <div className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">{order.customer_info?.name}</div>
                        <div className="text-xs text-gray-500">{order.customer_info?.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {/* Show membership purchase if present */}
                        {order.membership_purchase && (
                          <div className="text-xs text-[#FF6B35] font-medium">
                            {order.membership_purchase === 'pro' ? 'Just Dive Pro Membership' : 'Just Dive Club Membership'}
                          </div>
                        )}
                        {/* Show product items */}
                        {order.items && order.items.length > 0 ? (
                          <>
                            {order.items.slice(0, 2).map((item, idx) => (
                              <div key={idx} className="text-xs text-gray-600">{item.name} x{item.quantity}</div>
                            ))}
                            {order.items.length > 2 && <div className="text-xs text-gray-400">+{order.items.length - 2} more</div>}
                          </>
                        ) : !order.membership_purchase && (
                          <span className="text-xs text-gray-400 italic">No items</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {order.membership_purchase && (
                            <Badge variant="outline" className="text-xs border-[#FF6B35] text-[#FF6B35]">
                              {order.membership_purchase === 'pro' ? 'Pro' : 'Club'} Membership
                            </Badge>
                          )}
                          {order.items && order.items.length > 0 && (
                            [...new Set(order.items.map(item => getItemCategory(item)))].map((cat, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {cat}
                              </Badge>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">R{order.total.toFixed(0)}</td>
                      <td className="px-4 py-3">
                        <Select value={order.payment_status} onValueChange={(v) => updateOrderStatus(order.id, 'payment', v)}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending_payment">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={order.order_status} onValueChange={(v) => updateOrderStatus(order.id, 'order', v)}>
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            data-testid={`apply-promo-to-order-${order.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-[#FF6B35] hover:bg-orange-50"
                            onClick={() => openApplyPromoToOrder(order)}
                            title="Apply a promo credit retroactively (e.g. if it didn't auto-apply at checkout)"
                          >
                            <Gift className="h-4 w-4" />
                          </Button>
                          {order.order_status !== 'cancelled' && (
                            <Button
                              data-testid={`cancel-credit-order-${order.id}`}
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => cancelOrderAndCredit(order)}
                              title="Cancel this order and convert paid amount to store credit"
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-order-${order.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteOrder(order.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}
        {activeTab === 'invoices' && <InvoicesTab token={token} searchQuery={searchQuery} />}
        {activeTab === 'supplier-invoices' && <SupplierInvoicesTab token={token} />}

        {/* Memberships Tab */}
        {activeTab === 'memberships' && <MembershipsTab token={token} />}

        {/* Page Headers Tab */}
        {activeTab === 'page-headers' && <PageHeadersTab token={token} />}

        {/* Planner Operators Tab */}
        {activeTab === 'planner-ops' && <PlannerOperatorsTab token={token} />}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {/* Header with Add and Export Buttons */}
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h2 className="text-lg font-semibold">Members ({filteredMembers.length}{searchQuery || memberDateFrom || memberDateTo ? ` of ${members.length}` : ''})</h2>
              <div className="flex gap-2 flex-wrap items-center">
                {selectedMemberIds.size > 0 && (
                  <Button onClick={openGrantPromoModal} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white" data-testid="grant-promo-btn">
                    <Gift className="w-4 h-4 mr-2" />
                    Grant Promo to {selectedMemberIds.size} selected
                  </Button>
                )}
                <Button onClick={() => { setEditingUser(null); setUserForm({ email: '', first_name: '', last_name: '', contact_number: '', physical_address: '', password: '', membership_tier: '' }); setShowUserModal(true); }} data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add User
                </Button>
                <Button onClick={exportMembersCSV} variant="outline" data-testid="export-members-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
            {/* Date range filter */}
            <div className="flex flex-wrap items-center gap-2 bg-gray-50 border rounded-md p-3">
              <span className="text-sm font-medium text-gray-600">Joined between:</span>
              <Input
                type="date"
                value={memberDateFrom}
                onChange={(e) => setMemberDateFrom(e.target.value)}
                className="w-44"
                data-testid="member-date-from"
              />
              <span className="text-sm text-gray-500">and</span>
              <Input
                type="date"
                value={memberDateTo}
                onChange={(e) => setMemberDateTo(e.target.value)}
                className="w-44"
                data-testid="member-date-to"
              />
              {(memberDateFrom || memberDateTo) && (
                <Button size="sm" variant="ghost" onClick={() => { setMemberDateFrom(''); setMemberDateTo(''); }} data-testid="clear-date-filter">Clear</Button>
              )}
              <span className="text-xs text-gray-500 ml-auto">{filteredMembers.length} shown · {selectedMemberIds.size} selected</span>
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0A2342] text-white">
                    <tr>
                      <th className="px-2 py-3 text-center text-sm w-10">
                        <input
                          type="checkbox"
                          checked={filteredMembers.length > 0 && filteredMembers.every(m => selectedMemberIds.has(m.id))}
                          onChange={(e) => {
                            const next = new Set(selectedMemberIds);
                            if (e.target.checked) {
                              filteredMembers.forEach(m => next.add(m.id));
                            } else {
                              filteredMembers.forEach(m => next.delete(m.id));
                            }
                            setSelectedMemberIds(next);
                          }}
                          data-testid="select-all-members"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm">Name</th>
                      <th className="px-4 py-3 text-left text-sm">Email</th>
                      <th className="px-4 py-3 text-left text-sm">Phone</th>
                      <th className="px-4 py-3 text-left text-sm">Joined</th>
                      <th className="px-4 py-3 text-center text-sm">Membership</th>
                      <th className="px-4 py-3 text-center text-sm">Discount</th>
                      <th className="px-4 py-3 text-center text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          {searchQuery || memberDateFrom || memberDateTo ? 'No members match your filters' : 'No members yet'}
                        </td>
                      </tr>
                    ) : filteredMembers.map((member) => (
                      <tr key={member.id} className="border-b hover:bg-gray-50">
                        <td className="px-2 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedMemberIds.has(member.id)}
                            onChange={(e) => {
                              const next = new Set(selectedMemberIds);
                              if (e.target.checked) next.add(member.id);
                              else next.delete(member.id);
                              setSelectedMemberIds(next);
                            }}
                            data-testid={`select-member-${member.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium">{member.first_name} {member.last_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.contact_number || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{member.date_joined || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          {member.membership_tier ? (
                            <Badge className={member.membership_tier === 'pro' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                              {member.membership_tier.toUpperCase()}
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">None</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {member.custom_discount ? (
                            <Badge className="bg-green-100 text-green-800">{member.custom_discount}%</Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0" 
                              onClick={() => {
                                setEditingUser(member);
                                setUserForm({
                                  email: member.email,
                                  first_name: member.first_name,
                                  last_name: member.last_name,
                                  contact_number: member.contact_number || '',
                                  physical_address: member.physical_address || '',
                                  password: '',
                                  membership_tier: member.membership_tier || '',
                                  custom_discount: member.custom_discount || ''
                                });
                                setShowUserModal(true);
                              }}
                              title="Edit user"
                              data-testid={`edit-user-${member.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 h-8 w-8 p-0" 
                              onClick={() => deleteMember(member.id, member.email)}
                              title="Delete user"
                              data-testid={`delete-user-${member.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Products ({filteredProducts.length}{searchQuery ? ` of ${products.length}` : ''})</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API_URL}/admin/stock-export/excel`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `JustDive_Stock_${new Date().toISOString().split('T')[0]}.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Excel exported successfully');
                    } catch (error) {
                      toast.error('Failed to export Excel');
                    }
                  }}
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export Excel
                </Button>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API_URL}/admin/price-list/export`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `JustDive_Price_List_${new Date().toISOString().split('T')[0]}.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Price list exported successfully');
                    } catch (error) {
                      toast.error('Failed to export price list');
                    }
                  }}
                  data-testid="export-price-list-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export Price List
                </Button>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API_URL}/admin/stock-export/pdf`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `JustDive_Stock_${new Date().toISOString().split('T')[0]}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('PDF exported successfully');
                    } catch (error) {
                      toast.error('Failed to export PDF');
                    }
                  }}
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Export PDF
                </Button>
                <Button onClick={() => { setEditingProduct(null); setProductForm({ name: '', description: '', category: 'masks', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false, currency: 'ZAR', supplier_id: null }); setShowProductModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Product
                </Button>
              </div>
            </div>

            {/* Featured Rotation Settings */}
            <div className="bg-gradient-to-r from-yellow-50 to-white border border-yellow-200 rounded-lg p-4" data-testid="featured-rotation-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    Featured Products Rotation
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Picks 5 random products in New Gear + 5 in Pre-Dived every day.
                    {featuredSettings.last_run_at && (
                      <> Last run: <strong>{new Date(featuredSettings.last_run_at).toLocaleString()}</strong></>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="featured-auto-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!featuredSettings.enabled}
                      onChange={(e) => toggleFeaturedAuto(e.target.checked)}
                      disabled={featuredBusy}
                      className="cursor-pointer w-4 h-4"
                      data-testid="featured-auto-toggle"
                    />
                    <span className={featuredSettings.enabled ? 'text-green-700 font-medium' : 'text-gray-500'}>
                      Auto-randomise daily {featuredSettings.enabled ? '(on)' : '(off)'}
                    </span>
                  </label>
                  <Button
                    size="sm"
                    onClick={randomizeFeaturedNow}
                    disabled={featuredBusy}
                    className="bg-[#FF6B35] hover:bg-[#FF6B35]/90"
                    data-testid="randomize-featured-btn"
                  >
                    {featuredBusy ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working…</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-1" />Randomise Now</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border overflow-hidden">
              {/* Search + count row */}
              <div className="border-b px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="products-search-bar">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search products by name, description, category, brand…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-9 h-9 text-sm"
                    data-testid="products-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      data-testid="products-search-clear"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-600" data-testid="products-result-count">
                  {searchQuery || productCategoryFilter !== 'all' ? (
                    <>Showing <strong>{filteredProducts.length}</strong> of {products.length}</>
                  ) : (
                    <><strong>{products.length}</strong> products</>
                  )}
                </div>
                {/* Category filter (also enables reorder mode) */}
                <select
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  className="border rounded px-2 py-1 text-sm h-9"
                  data-testid="products-category-filter"
                  title="Filter by category — pick one to enable drag/drop reordering"
                >
                  <option value="all">All categories</option>
                  {distinctProductCategories.map((c) => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              {/* Reorder bar — only when a single category is selected */}
              {productCategoryFilter !== 'all' && (
                <ProductsReorderBar
                  products={filteredProducts}
                  token={token}
                  onSaved={fetchData}
                />
              )}
              {/* Bulk Edit Action Bar */}
              {selectedProductIds.size > 0 && (
                <div className="bg-orange-50 border-b border-orange-200 px-4 py-3 sticky top-0 z-10" data-testid="bulk-edit-bar">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="font-semibold text-[#0A2342]">
                      {selectedProductIds.size} selected
                    </div>
                    <button onClick={clearSelection} className="text-gray-500 hover:text-gray-700 underline" data-testid="bulk-clear-btn">clear</button>
                    <span className="text-gray-300">|</span>
                    {/* Category change */}
                    <div className="flex items-center gap-2">
                      <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="bulk-category-select">
                        <option value="">Change category to…</option>
                        <option value="regulators">Regulators</option>
                        <option value="bcds">BCDs</option>
                        <option value="computers">Dive Computers</option>
                        <option value="wetsuits">Wetsuits</option>
                        <option value="drysuits">Drysuits</option>
                        <option value="masks">Masks</option>
                        <option value="fins">Fins</option>
                        <option value="snorkels">Snorkels</option>
                        <option value="boots">Boots/Gloves</option>
                        <option value="cylinders">Cylinders</option>
                        <option value="knives">Knives</option>
                        <option value="torches">Torches</option>
                        <option value="bags">Bags</option>
                        <option value="buoys-reels">Buoys & Reels</option>
                        <option value="instruments">Instruments</option>
                        <option value="octos">Octos</option>
                        <option value="snorkeling">Snorkeling</option>
                        <option value="freediving">Freediving</option>
                        <option value="accessories">Accessories</option>
                        <option value="sets">Sets</option>
                      </select>
                      <Button size="sm" disabled={bulkBusy || !bulkCategory} onClick={handleBulkCategory} data-testid="bulk-category-apply">Apply</Button>
                    </div>
                    <span className="text-gray-300">|</span>
                    {/* In stock */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Stock:</span>
                      <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => handleBulkInStock(true)} data-testid="bulk-instock-on">In Stock</Button>
                      <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => handleBulkInStock(false)} data-testid="bulk-instock-off">Out</Button>
                    </div>
                    <span className="text-gray-300">|</span>
                    {/* Price change */}
                    <div className="flex items-center gap-2">
                      <select value={bulkPriceTarget} onChange={(e) => setBulkPriceTarget(e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="price">Sell</option>
                        <option value="cost_price">Cost</option>
                      </select>
                      <select value={bulkPriceMode} onChange={(e) => setBulkPriceMode(e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="percent">change by %</option>
                        <option value="flat">change by R</option>
                        <option value="set">set to R</option>
                        <option value="markup">set markup %</option>
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={bulkPriceMode === 'percent' ? '10 or -5' : '100 or -50'}
                        value={bulkPriceValue}
                        onChange={(e) => setBulkPriceValue(e.target.value)}
                        className="border rounded px-2 py-1 text-sm w-24"
                        data-testid="bulk-price-input"
                      />
                      <Button size="sm" disabled={bulkBusy || !bulkPriceValue} onClick={handleBulkPrice} data-testid="bulk-price-apply">Apply</Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0A2342] text-white">
                    <tr>
                      <th className="px-3 py-3 text-center text-sm w-10">
                        <input
                          type="checkbox"
                          checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProductIds.has(p.id))}
                          onChange={toggleSelectAllFiltered}
                          className="cursor-pointer"
                          data-testid="bulk-select-all"
                          aria-label="Select all"
                        />
                      </th>
                      <th className="px-4 py-3 text-center text-sm w-16">Order</th>
                      <th className="px-4 py-3 text-center text-sm w-12">
                        <Star className="w-4 h-4 mx-auto" />
                      </th>
                      <th className="px-4 py-3 text-left text-sm">Product</th>
                      <th className="px-4 py-3 text-left text-sm">Category</th>
                      <th className="px-4 py-3 text-right text-sm">Cost</th>
                      <th className="px-4 py-3 text-right text-sm">Price</th>
                      <th className="px-4 py-3 text-right text-sm">Profit</th>
                      <th className="px-4 py-3 text-right text-sm">Markup</th>
                      <th className="px-4 py-3 text-center text-sm">Stock</th>
                      <th className="px-4 py-3 text-center text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                          {searchQuery ? 'No products match your search' : 'No products yet'}
                        </td>
                      </tr>
                    ) : filteredProducts.map((product) => (
                      <tr key={product.id} className={`border-b hover:bg-gray-50 ${selectedProductIds.has(product.id) ? 'bg-orange-50' : ''}`}>
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedProductIds.has(product.id)}
                            onChange={() => toggleSelectProduct(product.id)}
                            className="cursor-pointer"
                            data-testid={`bulk-select-${product.id}`}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded text-sm font-medium">
                            {product.order || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleFeatured(product.id)}
                            className="hover:scale-110 transition-transform"
                            data-testid={`featured-toggle-${product.id}`}
                            title={product.featured ? 'Remove from Featured' : 'Add to Featured'}
                          >
                            <Star className={`w-5 h-5 ${product.featured ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img src={product.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                            <div>
                              <div className="font-medium text-sm">{product.name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-xs">{product.description?.slice(0, 50)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{product.category}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {product.cost_price ? `R${product.cost_price.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {product.price_on_request ? (
                            <span className="text-[#FF6B35] text-sm">On Request</span>
                          ) : (
                            `R${product.price.toLocaleString()}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {product.cost_price && !product.price_on_request ? (
                            <span className={`font-medium ${product.price - product.cost_price >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              R{(product.price - product.cost_price).toLocaleString()}
                              <span className="text-xs ml-1">({((product.price - product.cost_price) / product.price * 100).toFixed(0)}%)</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {product.cost_price && !product.price_on_request ? (
                            (() => {
                              const markup = ((product.price - product.cost_price) / product.cost_price) * 100;
                              const isDefault = Math.abs(markup - 20) < 0.5;
                              return (
                                <span className={`text-sm font-medium ${isDefault ? 'text-gray-500' : 'text-[#FF6B35]'}`} title={isDefault ? 'Default 20% markup' : 'Custom markup'}>
                                  {markup.toFixed(0)}%
                                </span>
                              );
                            })()
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {product.in_stock ? (
                            <Check className="w-5 h-5 text-green-600 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-600 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => {
                              setEditingProduct(product);
                              setProductForm({
                                name: product.name,
                                description: product.description,
                                category: product.category,
                                price: product.price.toString(),
                                cost_price: product.cost_price?.toString() || '',
                                image_url: product.image_url,
                                in_stock: product.in_stock,
                                sizes: product.sizes || [],
                                size_type: product.size_type || 'letter',
                                available_colors: product.available_colors || [],
                                custom_colors: product.custom_colors || [],
                                order: product.order || 0,
                                price_on_request: product.price_on_request || false,
                                currency: product.currency || 'ZAR',
                                supplier_id: product.supplier_id || null,
                              });
                              setShowProductModal(true);
                            }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteProduct(product.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Catalog Sync Tab */}
        {activeTab === 'catalog' && <CatalogSyncTab token={token} />}

        {/* Automation Tab */}
        {activeTab === 'automation' && <BrowseAutomationTab token={token} />}

        {/* Pre-Dived Tab */}
        {activeTab === 'predived' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Pre-Dived Gear ({filteredPredived.length}{searchQuery ? ` of ${predivedProducts.length}` : ''})</h2>
              <Button onClick={() => { setEditingPredived(null); setPredivedForm({ name: '', description: '', subcategory: 'bcds', price: '', cost_price: '', image_url: '', in_stock: true, sizes: [], size_type: 'letter', available_colors: [], custom_colors: [], order: 0, price_on_request: false }); setShowPredivedModal(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Add Pre-Dived Item
              </Button>
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
              {/* Bulk Edit Action Bar (Pre-Dived) — same behaviour as New Gear */}
              {selectedProductIds.size > 0 && (
                <div className="bg-orange-50 border-b border-orange-200 px-4 py-3 sticky top-0 z-10" data-testid="bulk-edit-bar-predived">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="font-semibold text-[#0A2342]">{selectedProductIds.size} selected</div>
                    <button onClick={clearSelection} className="text-gray-500 hover:text-gray-700 underline">clear</button>
                    <span className="text-gray-300">|</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Stock:</span>
                      <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => handleBulkInStock(true)}>In Stock</Button>
                      <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => handleBulkInStock(false)}>Out</Button>
                    </div>
                    <span className="text-gray-300">|</span>
                    <div className="flex items-center gap-2">
                      <select value={bulkPriceTarget} onChange={(e) => setBulkPriceTarget(e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="price">Sell</option>
                        <option value="cost_price">Cost</option>
                      </select>
                      <select value={bulkPriceMode} onChange={(e) => setBulkPriceMode(e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="percent">change by %</option>
                        <option value="flat">change by R</option>
                        <option value="set">set to R</option>
                        <option value="markup">set markup %</option>
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={bulkPriceMode === 'percent' ? '10 or -5' : '100 or -50'}
                        value={bulkPriceValue}
                        onChange={(e) => setBulkPriceValue(e.target.value)}
                        className="border rounded px-2 py-1 text-sm w-24"
                        data-testid="bulk-price-input-predived"
                      />
                      <Button size="sm" disabled={bulkBusy || !bulkPriceValue} onClick={handleBulkPrice} data-testid="bulk-price-apply-predived">Apply</Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0A2342] text-white">
                    <tr>
                      <th className="px-3 py-3 text-center text-sm w-10">
                        <input
                          type="checkbox"
                          checked={filteredPredived.length > 0 && filteredPredived.every(p => selectedProductIds.has(p.id))}
                          onChange={() => toggleSelectAllFiltered(filteredPredived)}
                          className="cursor-pointer"
                          data-testid="bulk-select-all-predived"
                          aria-label="Select all pre-dived"
                        />
                      </th>
                      <th className="px-4 py-3 text-center text-sm w-12">
                        <Star className="w-4 h-4 mx-auto" />
                      </th>
                      <th className="px-4 py-3 text-left text-sm">Item</th>
                      <th className="px-4 py-3 text-left text-sm">Type</th>
                      <th className="px-4 py-3 text-left text-sm">Size</th>
                      <th className="px-4 py-3 text-right text-sm">Price</th>
                      <th className="px-4 py-3 text-center text-sm">Stock</th>
                      <th className="px-4 py-3 text-center text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPredived.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          {searchQuery ? 'No pre-dived items match your search' : 'No pre-dived items yet'}
                        </td>
                      </tr>
                    ) : filteredPredived.map((item) => {
                      // Determine type from description
                      const desc = (item.description || '').toLowerCase();
                      let itemType = 'Other';
                      if (desc.includes('bcd')) itemType = 'BCD';
                      else if (desc.includes('regulator')) itemType = 'Regulator';
                      else if (desc.includes('wetsuit')) itemType = 'Wetsuit';
                      else if (desc.includes('fins')) itemType = 'Fins';
                      
                      return (
                        <tr key={item.id} className={`border-b hover:bg-gray-50 ${selectedProductIds.has(item.id) ? 'bg-orange-50' : ''}`}>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedProductIds.has(item.id)}
                              onChange={() => toggleSelectProduct(item.id)}
                              className="cursor-pointer"
                              data-testid={`predived-select-${item.id}`}
                              aria-label={`Select ${item.name}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => toggleFeatured(item.id)}
                              className="hover:scale-110 transition-transform"
                              title={item.featured ? 'Remove from Featured' : 'Add to Featured'}
                            >
                              <Star className={`w-5 h-5 ${item.featured ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <img src={item.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                              <div className="font-medium text-sm">{item.name}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{itemType}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">{item.sizes?.join(', ') || '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold">R{item.price.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            {item.in_stock ? (
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-red-600 mx-auto" />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-2">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditingPredived(item);
                                // Determine subcategory from description or stored subcategory
                                let subcat = item.subcategory || 'bcds';
                                if (!item.subcategory) {
                                  const d = (item.description || '').toLowerCase();
                                  if (d.includes('set') || d.includes('bundle') || d.includes('combo')) subcat = 'curated-sets';
                                  else if (d.includes('cylinder') || d.includes('tank')) subcat = 'cylinders';
                                  else if (d.includes('regulator')) subcat = 'regulators';
                                  else if (d.includes('wetsuit')) subcat = 'wetsuits';
                                  else if (d.includes('computer')) subcat = 'computers';
                                  else if (d.includes('mask')) subcat = 'masks';
                                  else if (d.includes('fins')) subcat = 'fins';
                                }
                                
                                setPredivedForm({
                                  name: item.name,
                                  description: item.description || '',
                                  subcategory: subcat,
                                  price: item.price.toString(),
                                  cost_price: item.cost_price?.toString() || '',
                                  image_url: item.image_url,
                                  in_stock: item.in_stock,
                                  sizes: item.sizes || [],
                                  size_type: item.size_type || 'letter',
                                  available_colors: item.available_colors || [],
                                  custom_colors: item.custom_colors || [],
                                  order: item.order || 0,
                                  price_on_request: item.price_on_request || false
                                });
                                setShowPredivedModal(true);
                              }}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePredived(item.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Trips Tab */}
        {activeTab === 'trips' && (
          <TripsAdminSection
            trips={trips}
            token={token}
            onSaved={fetchData}
            renderTripCard={(trip, handle, isFirst, isLast) => (
                <div key={trip.id} className={`bg-white rounded-lg border overflow-hidden ${trip.visible === false ? 'opacity-60 ring-1 ring-gray-300' : ''}`} data-testid={`admin-trip-${trip.id}`}>
                  <div className="relative">
                    <img src={trip.image_url} alt="" className="w-full h-40 object-cover" />
                    {trip.visible === false && (
                      <span
                        className="absolute top-2 left-2 text-[10px] font-bold tracking-wider uppercase bg-gray-900/85 text-white px-2 py-1 rounded"
                        data-testid={`trip-hidden-badge-${trip.id}`}
                      >
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {handle}
                      <h3 className="font-semibold flex-1">{trip.destination}</h3>
                      {trip.inquire_only ? (
                        <span className="font-bold text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Inquire-only</span>
                      ) : (
                        <span className="font-bold text-[#FF6B35]">R{trip.price.toLocaleString()}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{trip.description}</p>
                    <div className="text-xs text-gray-500 mb-3">
                      {trip.inquire_only ? (
                        <>Dates TBC • {trip.max_participants ? `Max ${trip.max_participants} pax` : 'No participant cap'}</>
                      ) : (
                        <>{trip.start_date} - {trip.end_date} • Max {trip.max_participants} pax</>
                      )}
                      {trip.available_on_pro && <span className="ml-2 text-purple-600 font-semibold">• PRO</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="flex-1 min-w-[110px] text-[#0A2342] border-[#0A2342]" onClick={() => setViewBookingsTrip(trip)} data-testid={`view-bookings-${trip.id}`}>
                        <Users className="w-4 h-4 mr-1" /> View Bookings
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                        setEditingTrip(trip);
                        setTripForm({
                          destination: trip.destination,
                          destinations_text: (trip.destinations || []).join('\n'),
                          description: trip.description,
                          start_date: trip.start_date,
                          end_date: trip.end_date,
                          price: trip.price.toString(),
                          image_url: trip.image_url,
                          max_participants: trip.max_participants,
                          available_on_pro: trip.available_on_pro || false,
                          dive_site: trip.dive_site || '',
                          sighting_possibilities: trip.sighting_possibilities || '',
                          typical_weather: trip.typical_weather || '',
                          area_activities: trip.area_activities || '',
                          accommodation_options: (trip.accommodation_options || []).map((o) => ({ name: o.name, price: o.price?.toString() ?? '', description: o.description || '', pricing_basis: o.pricing_basis || 'pps' })),
                          inclusions: trip.inclusions || '',
                          self_catering: trip.self_catering || false,
                          catered: trip.catered || false,
                          catered_price: trip.catered_price || 0,
                          nutshell: trip.nutshell || '',
                          inquire_only: trip.inquire_only || false,
                          show_starting_from: trip.show_starting_from || false,
                          currency: trip.currency || 'ZAR',
                          visible: trip.visible !== false,
                          supplier_id: trip.supplier_id || null,
                        });
                        setShowTripModal(true);
                      }}>
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTrip(trip.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
            )}
            onAddTrip={() => { setEditingTrip(null); setTripForm({ destination: '', destinations_text: '', description: '', start_date: '', end_date: '', price: '', image_url: '', max_participants: 12, available_on_pro: false, dive_site: '', sighting_possibilities: '', typical_weather: '', area_activities: '', accommodation_options: [], inclusions: '', self_catering: false, catered: false, catered_price: 0, nutshell: '', inquire_only: false, show_starting_from: false, currency: 'ZAR', visible: true }); setShowTripModal(true); }}
          />
        )}

        {/* Trip Inquiries Tab */}
        {activeTab === 'trip-inquiries' && <TripInquiriesTab token={token} />}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <CoursesAdminSection
            courses={filteredCourses}
            allCount={courses.length}
            searchQuery={searchQuery}
            token={token}
            onSaved={fetchData}
            onAdd={() => { setEditingCourse(null); setCourseForm({ name: '', description: '', price: '', level: 'Beginner', category: 'beginner', image_url: '', in_stock: true, min_age: '', what_to_expect: '', career_opportunities: '', fun_fact: '', currency: 'ZAR', supplier_id: null }); setShowCourseModal(true); }}
            onEdit={(course) => {
              setEditingCourse(course);
              setCourseForm({
                name: course.name,
                description: course.description,
                price: course.price.toString(),
                level: course.level,
                category: course.category,
                image_url: course.image_url || '',
                in_stock: course.in_stock,
                min_age: course.min_age?.toString() || '',
                what_to_expect: course.what_to_expect || '',
                career_opportunities: course.career_opportunities || '',
                fun_fact: course.fun_fact || '',
                currency: course.currency || 'ZAR',
                supplier_id: course.supplier_id || null,
              });
              setShowCourseModal(true);
            }}
            onDelete={deleteCourse}
          />
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Special Events ({filteredEvents.length}{searchQuery ? ` of ${events.length}` : ''})</h2>
              <Button onClick={() => { setEditingEvent(null); setEventForm({ name: '', description: '', event_date: '', end_date: '', event_time: '', price: '', price_starting_from: false, image_url: '', location: '', max_participants: 50, in_stock: true, what_to_expect: '', typical_weather: '', highlights: '', does_not_include: '', sessions: [], supplier_id: null }); setShowEventModal(true); }} data-testid="add-event-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Event
              </Button>
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0A2342] text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm">Event</th>
                      <th className="px-4 py-3 text-left text-sm">Date</th>
                      <th className="px-4 py-3 text-left text-sm">Location</th>
                      <th className="px-4 py-3 text-right text-sm">Price</th>
                      <th className="px-4 py-3 text-center text-sm">Active</th>
                      <th className="px-4 py-3 text-center text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          {searchQuery ? 'No events match your search' : 'No events yet'}
                        </td>
                      </tr>
                    ) : filteredEvents.map((event) => (
                      <tr key={event.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {event.image_url && (
                              <img src={event.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                            )}
                            <div>
                              <div className="font-medium text-sm flex items-center gap-2">
                                {event.name}
                                {event.is_draft && (
                                  <span className="text-[10px] uppercase tracking-wider bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">Draft</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-xs">{event.description?.slice(0, 50)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>{event.event_date}{event.end_date && ` - ${event.end_date}`}</div>
                          {event.event_time && <div className="text-xs text-gray-500">@ {event.event_time}</div>}
                          {event.sessions && event.sessions.length > 0 && (
                            <div className="text-xs text-[#FF6B35] mt-0.5">{event.sessions.length} session{event.sessions.length !== 1 ? 's' : ''}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{event.location || '-'}</td>
                        <td className="px-4 py-3 text-right font-semibold">{event.price_starting_from ? <span className="text-xs text-gray-500 font-normal mr-1">from</span> : null}R{event.price?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          {event.in_stock ? (
                            <Check className="w-5 h-5 text-green-600 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-600 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => duplicateEvent(event)} data-testid={`duplicate-event-${event.id}`} title="Duplicate event">
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setEditingEvent(event);
                              setEventForm({
                                name: event.name,
                                description: event.description,
                                event_date: event.event_date,
                                end_date: event.end_date || '',
                                event_time: event.event_time || '',
                                price: event.price.toString(),
                                price_starting_from: !!event.price_starting_from,
                                image_url: event.image_url || '',
                                location: event.location || '',
                                max_participants: event.max_participants || 50,
                                in_stock: event.in_stock,
                                what_to_expect: event.what_to_expect || '',
                                typical_weather: event.typical_weather || '',
                                highlights: event.highlights || '',
                                does_not_include: event.does_not_include || '',
                                sessions: (event.sessions || []).map(s => ({
                                  title: s.title || '',
                                  date: s.date || '',
                                  time: s.time || '',
                                  end_date: s.end_date || '',
                                  price: s.price !== null && s.price !== undefined ? s.price.toString() : '',
                                })),
                                supplier_id: event.supplier_id || null,
                              });
                              setShowEventModal(true);
                            }} data-testid={`edit-event-${event.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteEvent(event.id)} data-testid={`delete-event-${event.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && <CommunicationsTab token={token} />}

        {activeTab === 'newsletters' && <NewslettersTab token={token} />}

        {/* Forms Tab */}
        {activeTab === 'forms' && <FormsTab token={token} />}

        {/* Homepage Editor Tab */}
        {activeTab === 'homepage' && <HomepageTab token={token} />}

        {/* Popups (Floating Promo Ads) Tab */}
        {activeTab === 'popups' && <PopupsTab token={token} />}

        {/* Suppliers Tab */}
        {activeTab === 'suppliers' && <SuppliersTab token={token} />}

        {/* Promo Credits Management Tab */}
        {activeTab === 'promos' && <PromosTab token={token} />}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Site Settings</h2>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={() => window.open('https://mongodb-viewer-2.emergent.host/', '_blank')}
                  variant="outline" 
                  data-testid="view-database-btn"
                  className="text-[#0A2342] border-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                >
                  <Database className="w-4 h-4 mr-2" />
                  View Database
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
                <Button onClick={handleBackup} variant="outline" data-testid="backup-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Backup Database
                </Button>
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="save-settings-btn">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save All Changes
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <Input 
                      value={siteContent.contact_phone || ''} 
                      onChange={(e) => setSiteContent({...siteContent, contact_phone: e.target.value})}
                      placeholder="+27 67 535 5405"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <Input 
                      value={siteContent.contact_email || ''} 
                      onChange={(e) => setSiteContent({...siteContent, contact_email: e.target.value})}
                      placeholder="info@justdive.co.za"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                    <Input 
                      value={siteContent.contact_whatsapp || ''} 
                      onChange={(e) => setSiteContent({...siteContent, contact_whatsapp: e.target.value})}
                      placeholder="+27 67 535 5405"
                    />
                  </div>
                </div>
              </div>

              {/* Member Discount Tiers */}
              <div className="bg-white rounded-lg border p-6" data-testid="admin-discount-tiers-card">
                <h3 className="font-semibold text-[#0A2342] mb-1">Member Discount Tiers</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Percentages applied automatically at checkout. New gear and pre-dived gear are billed with <strong>separate</strong> tier rates — pre-dived defaults to 0%.
                </p>

                {/* New Gear */}
                <div className="border rounded-md p-4 mb-4 bg-emerald-50/40 border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                    <h4 className="font-semibold text-emerald-900">New Gear Discounts</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Club Member</label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={siteContent.discount_club_new ?? 20}
                          onChange={(e) => setSiteContent({ ...siteContent, discount_club_new: Number(e.target.value) })}
                          placeholder="20"
                          data-testid="discount-club-new"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Pro Member</label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={siteContent.discount_pro_new ?? 25}
                          onChange={(e) => setSiteContent({ ...siteContent, discount_pro_new: Number(e.target.value) })}
                          placeholder="25"
                          data-testid="discount-pro-new"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pre-Dived */}
                <div className="border rounded-md p-4 bg-amber-50/40 border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                    <h4 className="font-semibold text-amber-900">Pre-Dived Gear Discounts</h4>
                    <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">default 0%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Club Member</label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={siteContent.discount_club_predived ?? 0}
                          onChange={(e) => setSiteContent({ ...siteContent, discount_club_predived: Number(e.target.value) })}
                          placeholder="0"
                          data-testid="discount-club-predived"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Pro Member</label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={siteContent.discount_pro_predived ?? 0}
                          onChange={(e) => setSiteContent({ ...siteContent, discount_pro_predived: Number(e.target.value) })}
                          placeholder="0"
                          data-testid="discount-pro-predived"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-gray-400 mt-3">
                  Applied to gear only. Trips, courses, memberships and events are never discounted. Click <strong>Save all changes</strong> at the top to persist.
                </p>
              </div>


              {/* Social Media */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Social Media Links</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
                    <Input 
                      value={siteContent.facebook_url || ''} 
                      onChange={(e) => setSiteContent({...siteContent, facebook_url: e.target.value})}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
                    <Input 
                      value={siteContent.instagram_url || ''} 
                      onChange={(e) => setSiteContent({...siteContent, instagram_url: e.target.value})}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Sign-in Banner */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-1">Sign-in Banner</h3>
                <p className="text-xs text-gray-500 mb-4">Optional message shown above the login/register form. Fully under your control — turn it off whenever you like.</p>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="auth-banner-toggle">
                    <input
                      type="checkbox"
                      checked={String(siteContent.auth_banner_enabled || '').toLowerCase() === 'true'}
                      onChange={(e) => setSiteContent({ ...siteContent, auth_banner_enabled: e.target.checked ? 'true' : 'false' })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-[#0A2342]">Show banner to all visitors</span>
                  </label>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Banner text</label>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm"
                      rows={2}
                      value={siteContent.auth_banner_text || ''}
                      onChange={(e) => setSiteContent({ ...siteContent, auth_banner_text: e.target.value })}
                      placeholder="You've been referred! Sign up to claim your FREE Discover Scuba experience"
                      data-testid="auth-banner-text"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Tip: keep it short — 1-2 sentences max so it doesn&apos;t push the sign-in form below the fold.</p>
                  </div>
                </div>
              </div>

              {/* "Try Before You Buy" storefront panel toggle */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-1">"Try Before You Buy" Panel</h3>
                <p className="text-xs text-gray-500 mb-4">
                  The dark navy panel that appears above the product grid on the Products page and Torches page, listing your Aqua Divers &amp; Manex store addresses. Turn it off to hide it site-wide.
                </p>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="try-before-you-buy-toggle">
                  <input
                    type="checkbox"
                    checked={String(siteContent.try_before_you_buy_enabled ?? 'true').toLowerCase() !== 'false'}
                    onChange={(e) => setSiteContent({ ...siteContent, try_before_you_buy_enabled: e.target.checked ? 'true' : 'false' })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-[#0A2342]">Show the "Try Before You Buy" panel on the storefront</span>
                </label>
              </div>

              {/* Store Locations (Visit Us) */}
              <VisitUsLocationsCard
                locations={Array.isArray(siteContent.visit_us_locations) ? siteContent.visit_us_locations : []}
                onChange={(next) => setSiteContent({ ...siteContent, visit_us_locations: next })}
              />

              {/* Contact Us items (footer right column) */}
              <ContactUsItemsCard
                items={Array.isArray(siteContent.contact_us_items) ? siteContent.contact_us_items : []}
                onChange={(next) => setSiteContent({ ...siteContent, contact_us_items: next })}
              />

              {/* Homepage Content */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Homepage Content</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Title</label>
                    <Input 
                      value={siteContent.hero_title || ''} 
                      onChange={(e) => setSiteContent({...siteContent, hero_title: e.target.value})}
                      placeholder="Just Dive"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hero Subtitle</label>
                    <Input 
                      value={siteContent.hero_subtitle || ''} 
                      onChange={(e) => setSiteContent({...siteContent, hero_subtitle: e.target.value})}
                      placeholder="Your premier scuba diving destination"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">About Text</label>
                    <textarea 
                      className="w-full border rounded-md p-2 text-sm" 
                      rows={3}
                      value={siteContent.about_text || ''} 
                      onChange={(e) => setSiteContent({...siteContent, about_text: e.target.value})}
                      placeholder="About your business..."
                    />
                  </div>
                </div>
              </div>

              {/* Membership Pricing */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Membership Pricing</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Club Membership Price (R)</label>
                    <Input 
                      type="number"
                      value={siteContent.membership_club_price || ''} 
                      onChange={(e) => setSiteContent({...siteContent, membership_club_price: e.target.value})}
                      placeholder="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pro Membership Price (R)</label>
                    <Input 
                      type="number"
                      value={siteContent.membership_pro_price || ''} 
                      onChange={(e) => setSiteContent({...siteContent, membership_pro_price: e.target.value})}
                      placeholder="3600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                    <Input 
                      value={siteContent.footer_text || ''} 
                      onChange={(e) => setSiteContent({...siteContent, footer_text: e.target.value})}
                      placeholder="© 2026 Just Dive. All rights reserved."
                    />
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Banking Details (Checkout)</h3>
                <p className="text-xs text-gray-500 mb-4">These details appear on checkout and invoice emails</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <Input 
                      value={siteContent.bank_name || 'ABSA Bank'} 
                      onChange={(e) => setSiteContent({...siteContent, bank_name: e.target.value})}
                      placeholder="ABSA Bank"
                      data-testid="bank-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                    <Input 
                      value={siteContent.bank_account_name || 'JUST DIVE NPC'} 
                      onChange={(e) => setSiteContent({...siteContent, bank_account_name: e.target.value})}
                      placeholder="JUST DIVE NPC"
                      data-testid="bank-account-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <Input 
                      value={siteContent.bank_account_number || '4125123307'} 
                      onChange={(e) => setSiteContent({...siteContent, bank_account_number: e.target.value})}
                      placeholder="4125123307"
                      data-testid="bank-account-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                    <Input 
                      value={siteContent.bank_branch_code || '632005'} 
                      onChange={(e) => setSiteContent({...siteContent, bank_branch_code: e.target.value})}
                      placeholder="632005"
                      data-testid="bank-branch-code-input"
                    />
                  </div>
                </div>
              </div>

              {/* Database Export */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-[#0A2342] mb-4">Database Export</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Download a complete backup of all database collections including users, products, orders, forms, and more.
                </p>
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      try {
                        toast.info('Preparing database export...');
                        const res = await axios.get(`${API_URL}/admin/export-database`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        const content = JSON.stringify(res.data, null, 2);
                        const blob = new Blob([content], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `justdive-database-${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Database exported successfully');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to export database');
                      }
                    }}
                    data-testid="export-database-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Full Database (JSON)
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      window.open(`${API_URL}/admin/export-database`, '_blank');
                    }}
                    data-testid="view-database-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View in Browser
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  Export includes: Users, Products, Orders, Trips, Courses, Events, Brands, Medical Forms, Waivers, and Site Settings
                </p>
              </div>
            </div>

            {/* Notification Recipients */}
            <NotificationRecipientsCard token={token} />
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && viewingActivity && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">User Activity & Views</h2>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Filter by email..."
                  value={activityFilter}
                  onChange={(e) => setActivityFilter(e.target.value)}
                  className="w-64"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/admin/analytics/backfill-product-names`, {}, { headers: { Authorization: `Bearer ${token}` }});
                      toast.success('Product names updated');
                      fetchData();
                    } catch (e) {
                      toast.error('Failed to update');
                    }
                  }}
                >
                  Fix Missing Names
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">Total Views</span>
                </div>
                <p className="text-2xl font-bold text-[#0A2342]">{viewingActivity.total_views?.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Unique Viewers</span>
                </div>
                <p className="text-2xl font-bold text-[#0A2342]">{viewingActivity.top_viewers?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm">Products Viewed</span>
                </div>
                <p className="text-2xl font-bold text-[#0A2342]">{viewingActivity.top_products?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Peak Hour</span>
                </div>
                <p className="text-2xl font-bold text-[#0A2342]">
                  {viewingActivity.hourly_distribution?.reduce((max, h) => h.count > (max?.count || 0) ? h : max, {})._id || 0}:00
                </p>
              </div>
            </div>

            {/* Top Viewers */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
                  <Users className="w-4 h-4" /> Top Viewers (Most Active Users)
                </h3>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {viewingActivity.top_viewers?.filter(v => !activityFilter || v._id?.toLowerCase().includes(activityFilter.toLowerCase())).map((viewer, idx) => (
                  <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-sm">{viewer._id || 'Anonymous'}</p>
                      <p className="text-xs text-gray-500">Last view: {new Date(viewer.last_view).toLocaleString()}</p>
                    </div>
                    <Badge className="bg-[#FF6B35]">{viewer.view_count} views</Badge>
                  </div>
                ))}
                {viewingActivity.top_viewers?.length === 0 && (
                  <p className="p-4 text-gray-500 text-center">No user data yet</p>
                )}
              </div>
            </div>

            {/* Most Viewed Products */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Most Viewed Products
                </h3>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {viewingActivity.top_products?.map((product, idx) => (
                  <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-sm">{product.product_name || `ID: ${product._id?.slice(0, 8)}...`}</p>
                      <p className="text-xs text-gray-500">{product.product_category || 'uncategorized'} • {product.unique_user_count} unique viewers</p>
                    </div>
                    <Badge className="bg-[#0A2342]">{product.view_count} views</Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity Log */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Recent Activity (Who Viewed What & When)
                </h3>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/admin/analytics/backfill-product-names`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Product names refreshed');
                      // Reload data
                      const res = await axios.get(`${API_URL}/admin/analytics/views`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setViewingActivity(res.data);
                    } catch (error) {
                      toast.error('Failed to refresh product names');
                    }
                  }}
                  data-testid="refresh-activity-btn"
                >
                  Refresh Data
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium">Time</th>
                      <th className="text-left p-3 font-medium">User</th>
                      <th className="text-left p-3 font-medium">Product</th>
                      <th className="text-left p-3 font-medium">Category</th>
                      <th className="text-left p-3 font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {viewingActivity.recent_views?.filter(v => !activityFilter || v.user_email?.toLowerCase().includes(activityFilter.toLowerCase())).slice(0, 50).map((view, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="p-3 text-gray-600 whitespace-nowrap">
                          {new Date(view.timestamp).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <span className={view.user_email ? 'text-[#0A2342] font-medium' : 'text-gray-400 italic'}>
                            {view.user_email || 'Anonymous'}
                          </span>
                        </td>
                        <td className="p-3 font-medium">
                          {view.product_name && !view.product_name.match(/^Product [a-f0-9]+$/) 
                            ? view.product_name 
                            : <span className="text-gray-400 italic" title={`Product ID: ${view.product_id}`}>Deleted/Unknown product</span>
                          }
                        </td>
                        <td className="p-3 text-gray-600">{view.product_category || '-'}</td>
                        <td className="p-3 text-gray-600">{view.product_price ? `R${view.product_price.toLocaleString()}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Brands Tab */}
        {activeTab === 'brands' && <BrandsTab token={token} searchQuery={searchQuery} />}
        {/* Referrals Tab */}
        {activeTab === 'referrals' && <ReferralsTab token={token} />}
        </ErrorBoundary>
      </div>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Product Name" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} />
            <textarea 
              className="w-full border rounded-md p-2 text-sm" 
              rows={3} 
              placeholder="Description"
              value={productForm.description} 
              onChange={(e) => setProductForm({...productForm, description: e.target.value})} 
            />
            <div className="grid grid-cols-2 gap-4">
              <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="regulators">Regulators</SelectItem>
                  <SelectItem value="bcds">BCDs</SelectItem>
                  <SelectItem value="computers">Dive Computers</SelectItem>
                  <SelectItem value="wetsuits">Wetsuits</SelectItem>
                  <SelectItem value="drysuits">Drysuits</SelectItem>
                  <SelectItem value="masks">Masks</SelectItem>
                  <SelectItem value="fins">Fins</SelectItem>
                  <SelectItem value="snorkels">Snorkels</SelectItem>
                  <SelectItem value="boots">Boots</SelectItem>
                  <SelectItem value="gloves">Gloves</SelectItem>
                  <SelectItem value="cylinders">Cylinders</SelectItem>
                  <SelectItem value="knives">Knives</SelectItem>
                  <SelectItem value="torches">Torches</SelectItem>
                  <SelectItem value="bags">Bags</SelectItem>
                  <SelectItem value="buoys-reels">Buoys & Reels</SelectItem>
                  <SelectItem value="instruments">Instruments/Gauges</SelectItem>
                  <SelectItem value="octos">Octo's</SelectItem>
                  <SelectItem value="snorkeling">Snorkeling</SelectItem>
                  <SelectItem value="freediving">Freediving</SelectItem>
                  <SelectItem value="accessories">Accessories</SelectItem>
                  <SelectItem value="sets">Sets</SelectItem>
                </SelectContent>
              </Select>
              <Select value={productForm.currency || 'ZAR'} onValueChange={(v) => setProductForm({...productForm, currency: v})}>
                <SelectTrigger data-testid="product-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR — South African Rand (R)</SelectItem>
                  <SelectItem value="EUR">EUR — Euro (€)</SelectItem>
                  <SelectItem value="GBP">GBP — British Pound (£)</SelectItem>
                  <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price ({productForm.currency === 'EUR' ? '€' : productForm.currency === 'GBP' ? '£' : productForm.currency === 'USD' ? '$' : 'R'}) — what customers pay</label>
                <Input type="number" placeholder="0.00" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#FF6B35] mb-1">Cost Price (R) — what you pay supplier <span className="text-gray-500 font-normal">(drives markup)</span></label>
                <Input type="number" placeholder="0.00" value={productForm.cost_price} onChange={(e) => setProductForm({...productForm, cost_price: e.target.value})} />
              </div>
            </div>
            {/* Markup helper — quickly recalculate Selling Price from Cost using a markup % */}
            {productForm.cost_price && parseFloat(productForm.cost_price) > 0 && (
              <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 -mt-2" data-testid="markup-helper">
                <div className="text-xs text-gray-700 mb-2">
                  <strong>Markup helper</strong> — sets <strong>Selling Price</strong> = <strong>Cost (R{parseFloat(productForm.cost_price).toFixed(2)})</strong> × (1 + markup %).
                  <span className="block text-[11px] text-gray-500 mt-0.5">Want a different selling price? Update <strong>Cost Price</strong> first, then click a markup below.</span>
                </div>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-gray-600 text-xs">Apply:</span>
                  {[15, 20, 25, 30, 40, 50].map((m) => {
                    const cost = parseFloat(productForm.cost_price);
                    const result = (cost * (1 + m / 100)).toFixed(2);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          setProductForm({...productForm, price: result});
                        }}
                        className="px-2 py-1 rounded border border-gray-300 bg-white hover:bg-orange-100 hover:border-[#FF6B35] text-xs"
                        data-testid={`markup-preset-${m}`}
                      >{m}% → <strong>R{result}</strong></button>
                    );
                  })}
                  <span className="text-gray-300">|</span>
                  <span className="text-gray-600 text-xs">custom %:</span>
                  <input
                    type="number"
                    step="0.5"
                    placeholder="22.5"
                    className="border rounded px-2 py-1 text-xs w-20"
                    data-testid="markup-custom-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const m = parseFloat(e.target.value);
                        if (!isNaN(m)) {
                          const cost = parseFloat(productForm.cost_price);
                          setProductForm({...productForm, price: (cost * (1 + m / 100)).toFixed(2)});
                          e.target.value = '';
                        }
                      }
                    }}
                    title="Type a markup % and press Enter to recalculate the Selling Price"
                  />
                  {productForm.price && parseFloat(productForm.price) > 0 && (
                    <span className="text-xs text-gray-500 ml-auto">
                      current markup: <strong className="text-[#FF6B35]">{(((parseFloat(productForm.price) - parseFloat(productForm.cost_price)) / parseFloat(productForm.cost_price)) * 100).toFixed(1)}%</strong>
                    </span>
                  )}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
              <ImageUpload 
                value={productForm.image_url} 
                onChange={(url) => setProductForm({...productForm, image_url: url})} 
                token={token}
              />
            </div>
            
            {/* Color Picker */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <ColorPicker
                selectedColors={productForm.available_colors || []}
                customColors={productForm.custom_colors || []}
                onColorsChange={(colors) => setProductForm({...productForm, available_colors: colors})}
                onCustomColorsChange={(colors) => setProductForm({...productForm, custom_colors: colors})}
              />
            </div>
            
            {/* Size Picker */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <SizePicker
                selectedSizes={productForm.sizes || []}
                sizeType={productForm.size_type || 'letter'}
                onSizesChange={(sizes) => setProductForm({...productForm, sizes: sizes})}
                onSizeTypeChange={(type) => setProductForm({...productForm, size_type: type, sizes: []})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <Input type="number" placeholder="0" value={productForm.order} onChange={(e) => setProductForm({...productForm, order: e.target.value})} />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>
              <div className="space-y-2 mt-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={productForm.in_stock} onChange={(e) => setProductForm({...productForm, in_stock: e.target.checked})} />
                  <span className="text-sm">In Stock</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={productForm.price_on_request} onChange={(e) => setProductForm({...productForm, price_on_request: e.target.checked})} />
                  <span className="text-sm">Price on Request</span>
                </label>
              </div>
            </div>
            <SupplierPicker value={productForm.supplier_id} onChange={(v) => setProductForm({...productForm, supplier_id: v})} token={token} testId="product-supplier-picker" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)}>Cancel</Button>
            <Button onClick={handleProductSubmit}>{editingProduct ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trip Bookings Modal */}
      {viewBookingsTrip && (
        <TripBookingsModal
          trip={viewBookingsTrip}
          token={token}
          onClose={() => setViewBookingsTrip(null)}
        />
      )}

      {/* Trip Modal */}
      <Dialog open={showTripModal} onOpenChange={setShowTripModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTrip ? 'Edit Trip' : 'Add New Trip'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Destination" value={tripForm.destination} onChange={(e) => setTripForm({...tripForm, destination: e.target.value})} />
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <label className="block text-sm font-semibold text-[#0A2342] mb-1">
                Multi-destination options <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Running two locations on the same dates? List each destination on its own line and the trip card will show a &quot;Choose your destination&quot; dropdown. Leave blank for a normal single-destination trip.
              </p>
              <textarea
                className="w-full border rounded-md p-2 text-sm bg-white"
                rows={3}
                placeholder={"Sodwana Bay, South Africa\nPonta do Ouro, Mozambique"}
                value={tripForm.destinations_text || ''}
                onChange={(e) => setTripForm({ ...tripForm, destinations_text: e.target.value })}
                data-testid="trip-destinations-input"
              />
            </div>
            <textarea 
              className="w-full border rounded-md p-2 text-sm" 
              rows={3} 
              placeholder="Description"
              value={tripForm.description} 
              onChange={(e) => setTripForm({...tripForm, description: e.target.value})} 
            />

            {/* Visible / hidden — toggle a trip off the public site without deleting it */}
            <label className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <input
                type="checkbox"
                checked={tripForm.visible !== false}
                onChange={(e) => setTripForm({ ...tripForm, visible: e.target.checked })}
                className="w-4 h-4 mt-0.5 text-emerald-600"
                data-testid="trip-visible-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-emerald-900">
                  Visible on the public site
                </span>
                <p className="text-xs text-emerald-700">
                  Uncheck to hide this trip from the Trips page and block its shared link — without deleting it. Toggle it back on anytime to bring it back.
                </p>
              </div>
            </label>

            {/* Inquire-only toggle — for trips without firm dates yet */}
            <label className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <input
                type="checkbox"
                checked={!!tripForm.inquire_only}
                onChange={(e) => setTripForm({ ...tripForm, inquire_only: e.target.checked })}
                className="w-4 h-4 mt-0.5 text-amber-600"
                data-testid="trip-inquire-only-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-amber-900">Inquire-only (no firm dates yet)</span>
                <p className="text-xs text-amber-700">Shows "Inquire Now" instead of "Book Trip". Use this to gauge interest before locking dates. You can leave dates / price / max participants blank.</p>
              </div>
            </label>

            {/* Force "starting from" prefix on price (otherwise applied automatically when the trip has accommodation options) */}
            <label className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                checked={!!tripForm.show_starting_from}
                onChange={(e) => setTripForm({ ...tripForm, show_starting_from: e.target.checked })}
                className="w-4 h-4 mt-0.5 text-blue-600"
                data-testid="trip-show-starting-from-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-blue-900">Show "starting from" before price</span>
                <p className="text-xs text-blue-700">Useful when price varies by season, group size, or cabin tier. Already auto-applied for trips with multiple accommodation options.</p>
              </div>
            </label>

            {!tripForm.inquire_only && (
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Start Date (e.g. 19 Feb 2026)" value={tripForm.start_date} onChange={(e) => setTripForm({...tripForm, start_date: e.target.value})} />
                <Input placeholder="End Date (e.g. 22 Feb 2026)" value={tripForm.end_date} onChange={(e) => setTripForm({...tripForm, end_date: e.target.value})} />
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <Select value={tripForm.currency || 'ZAR'} onValueChange={(v) => setTripForm({...tripForm, currency: v})}>
                <SelectTrigger data-testid="trip-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR (R)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" placeholder={tripForm.inquire_only ? `Indicative price (${tripForm.currency === 'EUR' ? '€' : tripForm.currency === 'GBP' ? '£' : tripForm.currency === 'USD' ? '$' : 'R'}) — optional` : `Price (${tripForm.currency === 'EUR' ? '€' : tripForm.currency === 'GBP' ? '£' : tripForm.currency === 'USD' ? '$' : 'R'})`} value={tripForm.price} onChange={(e) => setTripForm({...tripForm, price: e.target.value})} />
              <Input type="number" placeholder={tripForm.inquire_only ? "Max participants — optional" : "Max Participants"} value={tripForm.max_participants} onChange={(e) => setTripForm({...tripForm, max_participants: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip Image</label>
              <ImageUpload 
                value={tripForm.image_url} 
                onChange={(url) => setTripForm({...tripForm, image_url: url})} 
                token={token}
              />
            </div>
            <label className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <input 
                type="checkbox" 
                checked={tripForm.available_on_pro} 
                onChange={(e) => setTripForm({...tripForm, available_on_pro: e.target.checked})}
                className="w-4 h-4 text-purple-600"
                data-testid="trip-available-on-pro-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-purple-800">Available on PRO</span>
                <p className="text-xs text-purple-600">Show PRO badge on this trip for Pro members</p>
              </div>
            </label>
            
            {/* Extended Trip Details */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-[#0A2342] mb-3">Extended Details (for "View More" section)</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Dive Site</label>
                  <Input 
                    placeholder="e.g. Ponta do Ouro, Tofo Beach" 
                    value={tripForm.dive_site} 
                    onChange={(e) => setTripForm({...tripForm, dive_site: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Sighting Possibilities</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Whale sharks, manta rays, dolphins, humpback whales"
                    value={tripForm.sighting_possibilities} 
                    onChange={(e) => setTripForm({...tripForm, sighting_possibilities: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Typical Weather</label>
                  <Input 
                    placeholder="e.g. Warm tropical, 24-28°C water temp" 
                    value={tripForm.typical_weather} 
                    onChange={(e) => setTripForm({...tripForm, typical_weather: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">What To Do Around The Area</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Beach walks, local markets, safari day trips"
                    value={tripForm.area_activities} 
                    onChange={(e) => setTripForm({...tripForm, area_activities: e.target.value})} 
                  />
                </div>
              </div>
            </div>

            {/* Accommodation Options (optional, multiple tiers) */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-[#0A2342]">Accommodation options (optional)</h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setTripForm({
                    ...tripForm,
                    accommodation_options: [
                      ...(tripForm.accommodation_options || []),
                      { name: '', price: '', description: '' },
                    ],
                  })}
                  data-testid="add-accommodation-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add option
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Add multiple tiers (e.g. Tented, Standard cabin, En-suite cabin). The headline price on the trip card uses the <strong>first option</strong> as "starting from" — drag rows or use ▲▼ to reorder. Leave empty for a single-price trip.
              </p>
              {(tripForm.accommodation_options || []).map((opt, i) => {
                const moveAccomm = (delta) => {
                  const list = [...(tripForm.accommodation_options || [])];
                  const j = i + delta;
                  if (j < 0 || j >= list.length) return;
                  [list[i], list[j]] = [list[j], list[i]];
                  setTripForm({ ...tripForm, accommodation_options: list });
                };
                const onDragStart = (e) => {
                  e.dataTransfer.setData('text/plain', String(i));
                  e.dataTransfer.effectAllowed = 'move';
                };
                const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
                const onDrop = (e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (isNaN(from) || from === i) return;
                  const list = [...(tripForm.accommodation_options || [])];
                  const [moved] = list.splice(from, 1);
                  list.splice(i, 0, moved);
                  setTripForm({ ...tripForm, accommodation_options: list });
                };
                const isFirst = i === 0;
                const isLast = i === (tripForm.accommodation_options || []).length - 1;
                return (
                <div
                  key={`acc-${i}`}
                  className="bg-slate-50 border rounded-lg p-3 mb-2 space-y-2"
                  data-testid={`accommodation-row-${i}`}
                  draggable
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                >
                  <div className="flex items-center gap-2">
                    {/* Reorder cluster */}
                    <div className="flex items-center gap-0.5 select-none" title="Drag to reorder, or use up/down arrows">
                      <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                      <button
                        type="button"
                        onClick={() => moveAccomm(-1)}
                        disabled={isFirst}
                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Move up"
                        data-testid={`accommodation-up-${i}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveAccomm(+1)}
                        disabled={isLast}
                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Move down"
                        data-testid={`accommodation-down-${i}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="text-xs text-gray-400 w-6 tabular-nums">#{i + 1}</span>
                    <Input
                      placeholder="Option name (e.g. Tented)"
                      value={opt.name}
                      onChange={(e) => {
                        const next = [...tripForm.accommodation_options];
                        next[i] = { ...next[i], name: e.target.value };
                        setTripForm({ ...tripForm, accommodation_options: next });
                      }}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Price (R)"
                      value={opt.price}
                      onChange={(e) => {
                        const next = [...tripForm.accommodation_options];
                        next[i] = { ...next[i], price: e.target.value };
                        setTripForm({ ...tripForm, accommodation_options: next });
                      }}
                      className="w-28"
                    />
                    <select
                      value={opt.pricing_basis || 'pps'}
                      onChange={(e) => {
                        const next = [...tripForm.accommodation_options];
                        next[i] = { ...next[i], pricing_basis: e.target.value };
                        setTripForm({ ...tripForm, accommodation_options: next });
                      }}
                      className="border rounded-md p-2 text-sm bg-white"
                      data-testid={`accommodation-basis-${i}`}
                      title="Pricing basis"
                    >
                      <option value="pps">pps (per person sharing)</option>
                      <option value="pp">pp (per person)</option>
                      <option value="single">single (solo room)</option>
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => {
                        const next = (tripForm.accommodation_options || []).filter((_, idx) => idx !== i);
                        setTripForm({ ...tripForm, accommodation_options: next });
                      }}
                      data-testid={`accommodation-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Description (e.g. Cabin with en-suite bathroom)"
                    value={opt.description || ''}
                    onChange={(e) => {
                      const next = [...tripForm.accommodation_options];
                      next[i] = { ...next[i], description: e.target.value };
                      setTripForm({ ...tripForm, accommodation_options: next });
                    }}
                  />
                </div>
                );
              })}
            </div>

            {/* Inclusions / Self-catering / Catered */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <h4 className="font-medium text-[#0A2342]">Inclusions</h4>
              <textarea
                className="w-full border rounded-md p-2 text-sm"
                rows={2}
                placeholder="What is included (e.g. 4 dives, conservation fees, levy)"
                value={tripForm.inclusions}
                onChange={(e) => setTripForm({ ...tripForm, inclusions: e.target.value })}
              />
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!tripForm.self_catering}
                    onChange={(e) => setTripForm({ ...tripForm, self_catering: e.target.checked })}
                    data-testid="trip-self-catering-checkbox"
                  /> Self-catering
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!tripForm.catered}
                    onChange={(e) => setTripForm({ ...tripForm, catered: e.target.checked })}
                    data-testid="trip-catered-checkbox"
                  /> Catered
                </label>
                {tripForm.catered && (
                  <div className="flex items-center gap-2 text-sm">
                    <label className="text-gray-600">Catering price (R)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-32"
                      placeholder="e.g. 450"
                      value={tripForm.catered_price ?? ''}
                      onChange={(e) => setTripForm({ ...tripForm, catered_price: e.target.value === '' ? 0 : Number(e.target.value) })}
                      data-testid="trip-catered-price"
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Tick both if the trip offers self-catering AND a catered upgrade. Both badges will show on the storefront.</p>
            </div>

            {/* In a Nutshell — short summary shown at the top of the
                storefront details panel when a diver clicks the trip. Line
                breaks are preserved on render. */}
            <div className="border-t pt-4 space-y-2">
              <h4 className="font-medium text-[#0A2342]">In a Nutshell</h4>
              <p className="text-xs text-gray-500">Short summary shown at the TOP of the trip details when a diver clicks &quot;More Info&quot;. Use bullet-style lines for scanability (e.g. &quot;• 4 dives · 3 nights · Ponta&quot;).</p>
              <textarea
                className="w-full border rounded-md p-2 text-sm"
                rows={4}
                placeholder={"e.g.\n• 3 nights, 4 dives\n• Departs Fri afternoon, back Sun evening\n• Suits OW+, min 10 logged dives\n• Nitrox available on request"}
                value={tripForm.nutshell || ''}
                onChange={(e) => setTripForm({ ...tripForm, nutshell: e.target.value })}
                data-testid="trip-nutshell-input"
              />
            </div>
            <SupplierPicker value={tripForm.supplier_id} onChange={(v) => setTripForm({...tripForm, supplier_id: v})} token={token} testId="trip-supplier-picker" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTripModal(false)}>Cancel</Button>
            <Button onClick={handleTripSubmit}>{editingTrip ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Dived Modal */}
      <Dialog open={showPredivedModal} onOpenChange={setShowPredivedModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingPredived ? 'Edit Pre-Dived Item' : 'Add Pre-Dived Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input placeholder="Product Name (e.g. AquaLung ProHD)" value={predivedForm.name} onChange={(e) => setPredivedForm({...predivedForm, name: e.target.value})} />
            <div className="grid grid-cols-2 gap-4">
              <Select value={predivedForm.subcategory} onValueChange={(v) => setPredivedForm({...predivedForm, subcategory: v})}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="curated-sets">Curated Set</SelectItem>
                  <SelectItem value="cylinders">Cylinder</SelectItem>
                  <SelectItem value="bcds">BCD</SelectItem>
                  <SelectItem value="regulators">Regulator</SelectItem>
                  <SelectItem value="computers">Dive Computer</SelectItem>
                  <SelectItem value="wetsuits">Wetsuit</SelectItem>
                  <SelectItem value="masks">Mask</SelectItem>
                  <SelectItem value="fins">Fins</SelectItem>
                  <SelectItem value="accessories">Accessory</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Selling Price (R)" value={predivedForm.price} onChange={(e) => setPredivedForm({...predivedForm, price: e.target.value})} />
              <Input type="number" placeholder="Cost Price (R) - Admin Only" value={predivedForm.cost_price} onChange={(e) => setPredivedForm({...predivedForm, cost_price: e.target.value})} />
            </div>
            <textarea 
              className="w-full border rounded-md p-2 text-sm" 
              rows={2} 
              placeholder="Description (optional - auto-generated if empty)"
              value={predivedForm.description} 
              onChange={(e) => setPredivedForm({...predivedForm, description: e.target.value})} 
            />
            
            {/* Color Picker */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <ColorPicker
                selectedColors={predivedForm.available_colors || []}
                customColors={predivedForm.custom_colors || []}
                onColorsChange={(colors) => setPredivedForm({...predivedForm, available_colors: colors})}
                onCustomColorsChange={(colors) => setPredivedForm({...predivedForm, custom_colors: colors})}
              />
            </div>
            
            {/* Size Picker */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <SizePicker
                selectedSizes={predivedForm.sizes || []}
                sizeType={predivedForm.size_type || 'letter'}
                onSizesChange={(sizes) => setPredivedForm({...predivedForm, sizes: sizes})}
                onSizeTypeChange={(type) => setPredivedForm({...predivedForm, size_type: type, sizes: []})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <Input type="number" placeholder="0" value={predivedForm.order} onChange={(e) => setPredivedForm({...predivedForm, order: e.target.value})} />
              </div>
              <div className="space-y-2 mt-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={predivedForm.in_stock} onChange={(e) => setPredivedForm({...predivedForm, in_stock: e.target.checked})} />
                  <span className="text-sm">In Stock</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={predivedForm.price_on_request} onChange={(e) => setPredivedForm({...predivedForm, price_on_request: e.target.checked})} />
                  <span className="text-sm">Price on Request</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Image</label>
              <ImageUpload 
                value={predivedForm.image_url} 
                onChange={(url) => setPredivedForm({...predivedForm, image_url: url})} 
                token={token}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPredivedModal(false)}>Cancel</Button>
            <Button onClick={handlePredivedSubmit}>{editingPredived ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Modal */}
      <Dialog open={showCourseModal} onOpenChange={setShowCourseModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input 
              placeholder="Course Name" 
              value={courseForm.name} 
              onChange={(e) => setCourseForm({...courseForm, name: e.target.value})} 
              data-testid="course-name-input"
            />
            <textarea 
              className="w-full border rounded-md p-2 text-sm" 
              rows={3} 
              placeholder="Description"
              value={courseForm.description} 
              onChange={(e) => setCourseForm({...courseForm, description: e.target.value})} 
              data-testid="course-description-input"
            />
            <div className="grid grid-cols-2 gap-4">
              <Select value={courseForm.level} onValueChange={(v) => setCourseForm({...courseForm, level: v})}>
                <SelectTrigger data-testid="course-level-select"><SelectValue placeholder="Level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Entry Level">Entry Level</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Specialty">Specialty</SelectItem>
                </SelectContent>
              </Select>
              <Select value={courseForm.category} onValueChange={(v) => setCourseForm({...courseForm, category: v})}>
                <SelectTrigger data-testid="course-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner Courses</SelectItem>
                  <SelectItem value="advanced">Advanced Courses</SelectItem>
                  <SelectItem value="professional">Professional (DM / Instructor)</SelectItem>
                  <SelectItem value="specialty">Specialty Courses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={courseForm.currency || 'ZAR'} onValueChange={(v) => setCourseForm({...courseForm, currency: v})}>
                <SelectTrigger data-testid="course-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR (R)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                type="number" 
                placeholder={`Price (${courseForm.currency === 'EUR' ? '€' : courseForm.currency === 'GBP' ? '£' : courseForm.currency === 'USD' ? '$' : 'R'})`}
                value={courseForm.price} 
                onChange={(e) => setCourseForm({...courseForm, price: e.target.value})} 
                data-testid="course-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Image (optional)</label>
              <ImageUpload 
                value={courseForm.image_url} 
                onChange={(url) => setCourseForm({...courseForm, image_url: url})} 
                token={token}
              />
            </div>
            <label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={courseForm.in_stock} 
                onChange={(e) => setCourseForm({...courseForm, in_stock: e.target.checked})} 
                data-testid="course-active-checkbox"
              />
              <span className="text-sm">Active (Available for booking)</span>
            </label>
            
            {/* Extended Course Details */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-[#0A2342] mb-3">Extended Details (for "View More" section)</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Minimum Age</label>
                  <Input 
                    type="number"
                    placeholder="e.g. 10" 
                    value={courseForm.min_age} 
                    onChange={(e) => setCourseForm({...courseForm, min_age: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">What to Expect</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Pool sessions, theory classes, open water dives"
                    value={courseForm.what_to_expect} 
                    onChange={(e) => setCourseForm({...courseForm, what_to_expect: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Career Opportunities</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Dive guide, instructor pathway"
                    value={courseForm.career_opportunities} 
                    onChange={(e) => setCourseForm({...courseForm, career_opportunities: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Fun Fact</label>
                  <Input 
                    placeholder="e.g. Most popular course worldwide" 
                    value={courseForm.fun_fact} 
                    onChange={(e) => setCourseForm({...courseForm, fun_fact: e.target.value})} 
                  />
                </div>
              </div>
            </div>
            <SupplierPicker value={courseForm.supplier_id} onChange={(v) => setCourseForm({...courseForm, supplier_id: v})} token={token} testId="course-supplier-picker" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourseModal(false)}>Cancel</Button>
            <Button onClick={handleCourseSubmit} data-testid="course-submit-btn">{editingCourse ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <Input 
              placeholder="Event Name" 
              value={eventForm.name} 
              onChange={(e) => setEventForm({...eventForm, name: e.target.value})} 
              data-testid="event-name-input"
            />
            <textarea 
              className="w-full border rounded-md p-2 text-sm" 
              rows={3} 
              placeholder="Description"
              value={eventForm.description} 
              onChange={(e) => setEventForm({...eventForm, description: e.target.value})} 
              data-testid="event-description-input"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
                <Input 
                  type="text"
                  placeholder="e.g. 15 Mar 2026" 
                  value={eventForm.event_date} 
                  onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})} 
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                <Input 
                  type="text"
                  placeholder="e.g. 16 Mar 2026" 
                  value={eventForm.end_date} 
                  onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})} 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Time (optional)</label>
                <Input 
                  type="text"
                  placeholder="e.g. 10:00" 
                  value={eventForm.event_time} 
                  onChange={(e) => setEventForm({...eventForm, event_time: e.target.value})} 
                  data-testid="event-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
                <Input 
                  placeholder="Location" 
                  value={eventForm.location} 
                  onChange={(e) => setEventForm({...eventForm, location: e.target.value})} 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (R)</label>
                <Input 
                  type="number" 
                  placeholder="Price" 
                  value={eventForm.price} 
                  onChange={(e) => setEventForm({...eventForm, price: e.target.value})} 
                  data-testid="event-price-input"
                />
              </div>
              <label className="flex items-center gap-2 pb-2">
                <input 
                  type="checkbox" 
                  checked={!!eventForm.price_starting_from} 
                  onChange={(e) => setEventForm({...eventForm, price_starting_from: e.target.checked})} 
                  data-testid="event-starting-from-checkbox"
                />
                <span className="text-sm">Show "Starting from" before price</span>
              </label>
            </div>
            <Input 
              type="number" 
              placeholder="Max Participants" 
              value={eventForm.max_participants} 
              onChange={(e) => setEventForm({...eventForm, max_participants: e.target.value})} 
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Image (optional)</label>
              <ImageUpload 
                value={eventForm.image_url} 
                onChange={(url) => setEventForm({...eventForm, image_url: url})} 
                token={token}
              />
            </div>
            <label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={eventForm.in_stock} 
                onChange={(e) => setEventForm({...eventForm, in_stock: e.target.checked})} 
                data-testid="event-active-checkbox"
              />
              <span className="text-sm">Active (Visible on website)</span>
            </label>
            
            {/* Extended Event Details */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-[#0A2342] mb-3">Extended Details (for "View More" section)</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">What to Expect</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Beach braai, night dive, social gathering"
                    value={eventForm.what_to_expect} 
                    onChange={(e) => setEventForm({...eventForm, what_to_expect: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Typical Weather</label>
                  <Input 
                    placeholder="e.g. Clear skies, warm evening" 
                    value={eventForm.typical_weather} 
                    onChange={(e) => setEventForm({...eventForm, typical_weather: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Highlights</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Live music, prizes, exclusive gear discounts"
                    value={eventForm.highlights} 
                    onChange={(e) => setEventForm({...eventForm, highlights: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Does Not Include (optional)</label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm" 
                    rows={2} 
                    placeholder="e.g. Gear rental, lunch, transport"
                    value={eventForm.does_not_include} 
                    onChange={(e) => setEventForm({...eventForm, does_not_include: e.target.value})} 
                    data-testid="event-does-not-include-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only displayed publicly if filled in.</p>
                </div>
              </div>
            </div>

            {/* Sessions (multi-day combo events) */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-[#0A2342]">Sessions (optional)</h4>
                <Button 
                  type="button"
                  size="sm" 
                  variant="outline"
                  onClick={() => setEventForm({
                    ...eventForm,
                    sessions: [...(eventForm.sessions || []), { title: '', date: '', time: '', end_date: '', price: '' }]
                  })}
                  data-testid="add-event-session-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Session
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Use sessions to combine multiple activities (e.g. "DS &amp; OW Pool" on day 1 + "OW Open Water Dives" on day 2) in a single event.</p>
              {(eventForm.sessions || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">No sessions added.</p>
              )}
              <div className="space-y-3">
                {(eventForm.sessions || []).map((session, idx) => (
                  <div key={idx} className="border rounded-md p-3 bg-gray-50 space-y-2" data-testid={`event-session-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#0A2342]">Session {idx + 1}</span>
                      <Button 
                        type="button"
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 h-7 px-2"
                        onClick={() => setEventForm({
                          ...eventForm,
                          sessions: eventForm.sessions.filter((_, i) => i !== idx)
                        })}
                        data-testid={`remove-event-session-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <Input 
                      placeholder="Session title (e.g. DS & OW Pool Sessions)"
                      value={session.title}
                      onChange={(e) => {
                        const updated = [...eventForm.sessions];
                        updated[idx] = { ...updated[idx], title: e.target.value };
                        setEventForm({ ...eventForm, sessions: updated });
                      }}
                      data-testid={`event-session-title-${idx}`}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        placeholder="Date (e.g. 6 Mar 2026)"
                        value={session.date}
                        onChange={(e) => {
                          const updated = [...eventForm.sessions];
                          updated[idx] = { ...updated[idx], date: e.target.value };
                          setEventForm({ ...eventForm, sessions: updated });
                        }}
                        data-testid={`event-session-date-${idx}`}
                      />
                      <Input 
                        placeholder="Time (e.g. 10:00)"
                        value={session.time}
                        onChange={(e) => {
                          const updated = [...eventForm.sessions];
                          updated[idx] = { ...updated[idx], time: e.target.value };
                          setEventForm({ ...eventForm, sessions: updated });
                        }}
                        data-testid={`event-session-time-${idx}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        placeholder="End date (optional)"
                        value={session.end_date}
                        onChange={(e) => {
                          const updated = [...eventForm.sessions];
                          updated[idx] = { ...updated[idx], end_date: e.target.value };
                          setEventForm({ ...eventForm, sessions: updated });
                        }}
                      />
                      <Input 
                        type="number"
                        placeholder="Price (R, optional)"
                        value={session.price}
                        onChange={(e) => {
                          const updated = [...eventForm.sessions];
                          updated[idx] = { ...updated[idx], price: e.target.value };
                          setEventForm({ ...eventForm, sessions: updated });
                        }}
                        data-testid={`event-session-price-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <SupplierPicker value={eventForm.supplier_id} onChange={(v) => setEventForm({...eventForm, supplier_id: v})} token={token} testId="event-supplier-picker" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>Cancel</Button>
            <Button onClick={handleEventSubmit} data-testid="event-submit-btn">{editingEvent ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              {editingUser && (
                <label className="block text-xs text-gray-500 mb-1">Email Address (admin can fix typos)</label>
              )}
              <Input 
                placeholder="Email Address" 
                type="email"
                value={userForm.email} 
                onChange={(e) => setUserForm({...userForm, email: e.target.value})} 
                data-testid="user-email-input"
              />
              {editingUser && userForm.email && userForm.email.toLowerCase() !== (editingUser.email || '').toLowerCase() && (
                <p className="text-xs text-orange-600 mt-1">⚠️ Changing the email will change this user's login credentials.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input 
                placeholder="First Name" 
                value={userForm.first_name} 
                onChange={(e) => setUserForm({...userForm, first_name: e.target.value})} 
                data-testid="user-firstname-input"
              />
              <Input 
                placeholder="Last Name" 
                value={userForm.last_name} 
                onChange={(e) => setUserForm({...userForm, last_name: e.target.value})} 
                data-testid="user-lastname-input"
              />
            </div>
            <Input 
              placeholder="Contact Number" 
              value={userForm.contact_number} 
              onChange={(e) => setUserForm({...userForm, contact_number: e.target.value})} 
            />
            <Input 
              placeholder="Physical Address" 
              value={userForm.physical_address} 
              onChange={(e) => setUserForm({...userForm, physical_address: e.target.value})} 
            />
            <Select value={userForm.membership_tier || 'none'} onValueChange={(v) => setUserForm({...userForm, membership_tier: v === 'none' ? '' : v})}>
              <SelectTrigger><SelectValue placeholder="Membership Tier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Membership</SelectItem>
                <SelectItem value="club">Club (R1,000/year)</SelectItem>
                <SelectItem value="pro">Pro (R3,600/year)</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Discount (%)</label>
              <Input 
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 15 for 15% off" 
                value={userForm.custom_discount} 
                onChange={(e) => setUserForm({...userForm, custom_discount: e.target.value})} 
                data-testid="user-custom-discount-input"
              />
              <p className="text-xs text-gray-500 mt-1">Individual discount applied to all products. Higher of membership or custom discount is used.</p>
            </div>
            <div>
              <Input 
                placeholder={editingUser ? "New Password (leave blank to keep current)" : "Password"} 
                type="password"
                value={userForm.password} 
                onChange={(e) => setUserForm({...userForm, password: e.target.value})} 
                data-testid="user-password-input"
              />
              {editingUser && (
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
            <Button onClick={handleUserSubmit} data-testid="user-submit-btn">{editingUser ? 'Update User' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Order Modal */}
      <ManualOrderModal
        open={showManualOrderModal}
        onOpenChange={setShowManualOrderModal}
        token={token}
        onCreated={() => { fetchData(); }}
      />

      {/* Apply Promo to Existing Order Modal — for fixing failed redemptions */}
      <Dialog open={!!applyPromoOrder} onOpenChange={(open) => { if (!open) { setApplyPromoOrder(null); setApplyPromoCredits([]); setForceApplyEnabled(false); setForceItemIndex(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#FF6B35]" />
              Apply Promo Credit to Order
            </DialogTitle>
          </DialogHeader>
          {applyPromoOrder && (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded p-3 text-sm">
                <div className="font-mono text-xs text-gray-500 mb-1">Order #{applyPromoOrder.id.slice(0, 8).toUpperCase()}</div>
                <div className="font-semibold">{applyPromoOrder.user_email || applyPromoOrder.user_id}</div>
                <div className="text-gray-600 mt-1">Current total: <strong>R{Number(applyPromoOrder.total).toFixed(2)}</strong></div>
                {applyPromoOrder.items?.map((it, i) => (
                  <div key={i} className="text-xs text-gray-500 mt-1">• {it.name} (R{it.price})</div>
                ))}
              </div>
              {applyPromoCredits.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  ⚠️ This user has no unused promo credits. <strong>Grant one first</strong> via Members tab → Grant Promo.
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Choose credit to apply</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {applyPromoCredits.map((c) => (
                      <label key={c.id} className={`flex items-start gap-2 p-3 rounded border cursor-pointer ${selectedCreditId === c.id ? 'border-[#FF6B35] bg-orange-50' : ''}`} data-testid={`select-credit-${c.id}`}>
                        <input type="radio" name="credit" value={c.id} checked={selectedCreditId === c.id} onChange={() => setSelectedCreditId(c.id)} className="mt-1" />
                        <div className="text-sm flex-1">
                          <div className="font-medium text-[#0A2342]">
                            {c.type === 'free_item' ? '🎁 Free Item' : `R${Number(c.value).toLocaleString()} Off`}
                            {c.applies_to_category && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{c.applies_to_category}</span>}
                          </div>
                          {c.reason && <div className="text-xs text-gray-600">{c.reason}</div>}
                          <div className="text-xs text-gray-400 mt-0.5">Granted {c.created_at ? new Date(c.created_at).toLocaleDateString('en-ZA') : ''}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3 italic">
                    Applying will recompute the order total and flag it as refund-due if the new total is less than what they paid.
                  </p>

                  {/* Force-apply override — for cases where the credit was created for one
                      item but the customer purchased a different/related one (e.g.
                      "Free Discover Scuba" credit redeemed on the JULY Combo event). */}
                  <div className="mt-4 border-t pt-3">
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="toggle-force-apply">
                      <input
                        type="checkbox"
                        checked={forceApplyEnabled}
                        onChange={(e) => { setForceApplyEnabled(e.target.checked); if (!e.target.checked) setForceItemIndex(null); }}
                        className="mt-1"
                      />
                      <div className="text-sm">
                        <div className="font-medium text-[#0A2342]">Force apply to a specific line</div>
                        <div className="text-xs text-gray-500">Use when the credit's product doesn't exactly match anything in this order. Overrides normal id/category matching.</div>
                      </div>
                    </label>
                    {forceApplyEnabled && (
                      <div className="mt-3 space-y-1 pl-6">
                        {(applyPromoOrder.items || []).map((it, idx) => (
                          <label key={idx} className={`flex items-center gap-2 p-2 rounded border text-sm cursor-pointer ${forceItemIndex === idx ? 'border-[#FF6B35] bg-orange-50' : ''}`} data-testid={`force-line-${idx}`}>
                            <input
                              type="radio"
                              name="force-item"
                              checked={forceItemIndex === idx}
                              onChange={() => setForceItemIndex(idx)}
                            />
                            <span className="flex-1">{it.name}</span>
                            <span className="text-xs text-gray-500">R{Number(it.price || 0).toFixed(2)} × {it.quantity || 1}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApplyPromoOrder(null); setApplyPromoCredits([]); setForceApplyEnabled(false); setForceItemIndex(null); }}>Cancel</Button>
            {applyPromoCredits.length > 0 && (
              <Button onClick={confirmApplyPromoToOrder} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white" data-testid="confirm-apply-promo-to-order">
                Apply Credit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Promo Credits Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#FF6B35]" />
              Grant Promo Credit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 border rounded p-3 text-sm">
              <p className="font-semibold text-[#0A2342]">{selectedMemberIds.size} user{selectedMemberIds.size !== 1 ? 's' : ''} selected</p>
              <p className="text-gray-600 text-xs mt-1">Each user will receive an individual credit. Credits are single-use, auto-applied at checkout, and won't show to the customer until they reach the checkout page.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Credit Type</label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`border rounded p-3 cursor-pointer text-sm ${promoForm.type === 'free_item' ? 'border-[#FF6B35] bg-orange-50' : ''}`}>
                  <input type="radio" name="promo_type" value="free_item" checked={promoForm.type === 'free_item'} onChange={() => setPromoForm({ ...promoForm, type: 'free_item' })} className="mr-2" data-testid="promo-type-free-item" />
                  <strong>Free Item</strong>
                  <div className="text-xs text-gray-500 mt-1">100% off a specific course/product</div>
                </label>
                <label className={`border rounded p-3 cursor-pointer text-sm ${promoForm.type === 'fixed_amount' ? 'border-[#FF6B35] bg-orange-50' : ''}`}>
                  <input type="radio" name="promo_type" value="fixed_amount" checked={promoForm.type === 'fixed_amount'} onChange={() => setPromoForm({ ...promoForm, type: 'fixed_amount' })} className="mr-2" data-testid="promo-type-fixed-amount" />
                  <strong>Amount Off</strong>
                  <div className="text-xs text-gray-500 mt-1">e.g. R1000 off any course</div>
                </label>
              </div>
            </div>

            {promoForm.type === 'free_item' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Choose item to make free</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={promoForm.free_item_id}
                  onChange={(e) => {
                    const all = [...courses, ...events, ...products];
                    const found = all.find(x => x.id === e.target.value);
                    setPromoForm({
                      ...promoForm,
                      free_item_id: e.target.value,
                      free_item_label: found ? (found.name || found.destination || found.id) : '',
                    });
                  }}
                  data-testid="promo-free-item-select"
                >
                  <option value="">— Select item —</option>
                  <optgroup label="Courses">
                    {courses.map(c => <option key={c.id} value={c.id}>{c.name} (R{c.price})</option>)}
                  </optgroup>
                  <optgroup label="Events">
                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name} (R{ev.price})</option>)}
                  </optgroup>
                </select>
              </div>
            )}

            {promoForm.type === 'fixed_amount' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount off (R)</label>
                  <Input type="number" placeholder="e.g. 1000" value={promoForm.amount} onChange={(e) => setPromoForm({ ...promoForm, amount: e.target.value })} data-testid="promo-amount-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Restrict to category (optional)</label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm" value={promoForm.applies_to_category} onChange={(e) => setPromoForm({ ...promoForm, applies_to_category: e.target.value })} data-testid="promo-category-select">
                    <option value="">— Any item —</option>
                    <option value="course">Courses only</option>
                    <option value="event">Events only</option>
                    <option value="trip">Trips only</option>
                    <option value="product">Products only</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry date (optional)</label>
              <Input type="date" value={promoForm.expires_at} onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })} data-testid="promo-expiry-input" />
              <p className="text-xs text-gray-500 mt-1">Leave blank for no expiry.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Note</label>
              <Input placeholder="e.g. Sign-up promo: free Discover Scuba" value={promoForm.reason} onChange={(e) => setPromoForm({ ...promoForm, reason: e.target.value })} data-testid="promo-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoModal(false)}>Cancel</Button>
            <Button onClick={handleGrantPromo} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white" data-testid="confirm-grant-promo-btn">
              Grant to {selectedMemberIds.size} user{selectedMemberIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ---------- Reorderable section helpers ----------

const ProductsReorderBar = ({ products, token, onSaved }) => {
  const [open, setOpen] = useState(false);
  const r = useReorderable(products, '/admin/products/reorder', token, onSaved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { r.reset(products); }, [products.map((p) => p.id).join('|')]);
  return (
    <div className="border-b bg-purple-50" data-testid="products-reorder-bar">
      <div className="px-4 py-2 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-purple-900">
          <strong>Reorder mode</strong> — {products.length} product{products.length === 1 ? '' : 's'} in this category. Drag rows or use ▲▼ to change their position on the storefront.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-xs px-2 py-1 border border-purple-300 bg-white rounded hover:bg-purple-100"
            data-testid="products-reorder-toggle"
          >
            {open ? 'Hide reorder list' : 'Show reorder list'}
          </button>
        </div>
      </div>
      {open && (
        <>
          <ReorderSaveBar dirty={r.dirty} saving={r.saving} onSave={r.commit} label="product order" />
          <div className="max-h-[480px] overflow-y-auto bg-white border-t" data-testid="products-reorder-list">
            {r.orderedItems.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-2 border-b hover:bg-gray-50" data-testid={`reorder-product-row-${p.id}`}>
                <ReorderHandle
                  id={p.id}
                  onUp={r.moveUp}
                  onDown={r.moveDown}
                  onDragStart={r.handleDragStart}
                  onDragOver={r.handleDragOver}
                  onDragEnd={r.handleDragEnd}
                  isFirst={i === 0}
                  isLast={i === r.orderedItems.length - 1}
                  testIdPrefix="product-reorder"
                />
                <span className="text-xs text-gray-400 w-8 tabular-nums">#{i + 1}</span>
                {p.image_url && (<img src={p.image_url} alt="" className="w-8 h-8 object-cover rounded" />)}
                <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                {p.brand && <span className="text-xs text-gray-500 hidden md:inline">{p.brand}</span>}
                <span className="text-xs text-gray-500 tabular-nums">
                  {p.price_on_request ? 'on req.' : `R${p.price?.toLocaleString()}`}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


const TripsAdminSection = ({ trips, token, onSaved, renderTripCard, onAddTrip }) => {
  const r = useReorderable(trips, '/admin/trips/reorder', token, onSaved);
  // Refresh local sequence whenever the upstream list changes
  // (e.g. after add / delete / refetch).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { r.reset(trips); }, [trips.map((t) => t.id).join('|')]);
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Dive Trips ({trips.length})</h2>
        <Button onClick={onAddTrip} data-testid="add-trip-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Trip
        </Button>
      </div>
      <ReorderSaveBar dirty={r.dirty} saving={r.saving} onSave={r.commit} label="trip order" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {r.orderedItems.map((trip, i) => {
          const handle = (
            <ReorderHandle
              id={trip.id}
              onUp={r.moveUp}
              onDown={r.moveDown}
              onDragStart={r.handleDragStart}
              onDragOver={r.handleDragOver}
              onDragEnd={r.handleDragEnd}
              isFirst={i === 0}
              isLast={i === r.orderedItems.length - 1}
              testIdPrefix="trip-reorder"
            />
          );
          return renderTripCard(trip, handle, i === 0, i === r.orderedItems.length - 1);
        })}
      </div>
    </div>
  );
};


const CoursesAdminSection = ({ courses, allCount, searchQuery, token, onSaved, onAdd, onEdit, onDelete }) => {
  const r = useReorderable(courses, '/admin/courses/reorder', token, onSaved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { r.reset(courses); }, [courses.map((c) => c.id).join('|')]);
  const reorderDisabled = !!searchQuery;
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Dive Courses ({courses.length}{searchQuery ? ` of ${allCount}` : ''})</h2>
        <Button onClick={onAdd} data-testid="add-course-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Course
        </Button>
      </div>
      {reorderDisabled && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          Clear the search to reorder courses.
        </div>
      )}
      {!reorderDisabled && <ReorderSaveBar dirty={r.dirty} saving={r.saving} onSave={r.commit} label="course order" />}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A2342] text-white">
              <tr>
                {!reorderDisabled && <th className="px-2 py-3 text-left text-sm w-10">Order</th>}
                <th className="px-4 py-3 text-left text-sm">Course</th>
                <th className="px-4 py-3 text-left text-sm">Level</th>
                <th className="px-4 py-3 text-left text-sm">Category</th>
                <th className="px-4 py-3 text-right text-sm">Price</th>
                <th className="px-4 py-3 text-center text-sm">Active</th>
                <th className="px-4 py-3 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {r.orderedItems.length === 0 ? (
                <tr>
                  <td colSpan={reorderDisabled ? 6 : 7} className="px-4 py-8 text-center text-gray-500">
                    {searchQuery ? 'No courses match your search' : 'No courses yet'}
                  </td>
                </tr>
              ) : r.orderedItems.map((course, i) => (
                <tr key={course.id} className="border-b hover:bg-gray-50" data-testid={`admin-course-row-${course.id}`}>
                  {!reorderDisabled && (
                    <td className="px-2 py-3">
                      <ReorderHandle
                        id={course.id}
                        onUp={r.moveUp}
                        onDown={r.moveDown}
                        onDragStart={r.handleDragStart}
                        onDragOver={r.handleDragOver}
                        onDragEnd={r.handleDragEnd}
                        isFirst={i === 0}
                        isLast={i === r.orderedItems.length - 1}
                        testIdPrefix="course-reorder"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {course.image_url && (<img src={course.image_url} alt="" className="w-10 h-10 object-cover rounded" />)}
                      <div>
                        <div className="font-medium text-sm">{course.name}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">{course.description?.slice(0, 50)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={
                      course.level === 'Professional' ? 'border-purple-500 text-purple-700' :
                      course.level === 'Advanced' ? 'border-red-500 text-red-700' :
                      course.level === 'Intermediate' ? 'border-orange-500 text-orange-700' :
                      'border-green-500 text-green-700'
                    }>{course.level}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">{course.category}</td>
                  <td className="px-4 py-3 text-right font-semibold">R{course.price?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">{course.in_stock ? <Check className="w-5 h-5 text-green-600 mx-auto" /> : <X className="w-5 h-5 text-red-600 mx-auto" />}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => onEdit(course)} data-testid={`edit-course-${course.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(course.id)} data-testid={`delete-course-${course.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


export default AdminPage;
